import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SMS_PACKS } from '../data/smsPacks';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useCountUp from '../hooks/useCountUp';
import { localDateStr, contactAge } from '../utils/dateUtils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useApp } from '../context/AppContext';
import { Card, CardHeader } from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';
import { Input, Select, Textarea } from '../components/UI/Input';
import { api } from '../services/api';
import { isVip } from '../utils/segments';
import { smsInfo, smsFromEmailBody } from '../utils/sms';
import { autoCampaignName } from '../utils/campaignName';
import { swissCantons } from '../utils/cantons';
import { isSwissMobile } from '../utils/dataInsights';
import ConfirmModal from '../components/UI/ConfirmModal';
import Breadcrumb from '../components/Breadcrumb';
import GenieTip from '../components/GenieTip';
import SectionHint from '../components/SectionHint';
import './Campaigns.css';


const objectives = [
  { id: 'fidelize',    icon: '💛', title: 'Fidéliser mes clients',              color: '#FFC400' },
  { id: 'reactivate',  icon: '💤', title: 'Réactiver les clients dormants',      color: '#7B61FF' },
  { id: 'revenue',     icon: '📈', title: "Augmenter mon chiffre d'affaires",    color: '#10B981' },
  { id: 'appointments',icon: '📅', title: 'Générer plus de rendez-vous',         color: '#5A35FF' },
  { id: 'enrich',      icon: '🗂️', title: 'Améliorer ma base client',            color: '#F59E0B' },
  { id: 'know',        icon: '🔍', title: 'Mieux connaître ma clientèle',         color: '#06B6D4' },
  { id: 'birthday',    icon: '🎂', title: 'Créer une campagne anniversaire',      color: '#FF4FA3' },
  { id: 'vip',         icon: '⭐', title: 'Détecter mes meilleurs clients',       color: '#F59E0B' },
  { id: 'local',       icon: '📍', title: 'Créer une campagne locale',            color: '#10B981' },
  { id: 'promo',       icon: '🎯', title: 'Lancer une offre ou une promotion',    color: '#EF4444' },
  // Saisonnières
  { id: 'noel',        icon: '🎄', title: 'Vœux de Noël',                        color: '#DC2626', seasonal: true },
  { id: 'newyear',     icon: '🥂', title: 'Bonne année — vœux Nouvel An',        color: '#7C3AED', seasonal: true },
  { id: 'paques',      icon: '🐣', title: 'Joyeuses Pâques',                     color: '#059669', seasonal: true },
  { id: 'fetesMeres',  icon: '💐', title: 'Fête des Mères',                      color: '#DB2777', seasonal: true },
  { id: 'fetesPeres',  icon: '👔', title: 'Fête des Pères',                      color: '#2563EB', seasonal: true },
  { id: 'rentree',     icon: '🍂', title: 'Rentrée de septembre',                color: '#D97706', seasonal: true },
  { id: 'printemps',   icon: '🌸', title: 'Offre de printemps',                  color: '#16A34A', seasonal: true },
  { id: 'ete',         icon: '☀️', title: 'Promotion estivale',                  color: '#EA580C', seasonal: true },
];

const statusLabels = { sent: { label: 'Envoyée', variant: 'green' }, sending: { label: '🔄 Envoi en cours…', variant: 'blue' }, draft: { label: 'Brouillon', variant: 'gray' }, scheduled: { label: 'Planifiée', variant: 'blue' } };

/* ── Campaign detail full overlay ── */
function CampaignDetailOverlay({ campaign, onClose, onSend, onAddNote, onUpdateReport }) {
  const { addNotification: notifyShare } = useApp();
  const navigate = useNavigate();
  const s = statusLabels[campaign.status] || statusLabels.draft;
  const [noteText, setNoteText] = useState('');
  // On atterrit sur le Rapport dès qu'une campagne est envoyée (c'est ce qui intéresse) ;
  // sinon (brouillon/planifiée) sur le Journal de bord.
  const [tab, setTab] = useState(() => (campaign.status === 'sent' ? 'rapport' : 'journal')); // 'journal' | 'rapport'
  const [generatingReport, setGeneratingReport] = useState(false);

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    const note = {
      id: Date.now(),
      date: format(new Date(), 'yyyy-MM-dd HH:mm'),
      text: noteText.trim(),
    };
    onAddNote(campaign.id, note);
    setNoteText('');
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const data = await api.generateCampaignReport(campaign.id);
      onUpdateReport(campaign.id, data.report);
      setTab('rapport');
    } catch (err) {
      console.error('Report generation failed', err);
    } finally {
      setGeneratingReport(false);
    }
  };

  const rate = campaign.open_rate || 0;
  const rateColor = rate >= 35 ? '#10B981' : rate >= 15 ? '#F59E0B' : '#EF4444';
  // Hero TOUJOURS aux couleurs de la marque (violet) — le statut vit dans la pastille,
  // pas dans un dégradé vert/bleu qui casse l'identité de l'app.
  const heroGradient = 'linear-gradient(135deg,#5A35FF 0%,#7C3AED 50%,#2563EB 100%)';

  const channelLabel = campaign.channel === 'sms' ? 'SMS'
    : (campaign.channel === 'email+sms' || campaign.channel === 'email_sms') ? 'E-mail + SMS'
    : 'E-mail';
  const sentDateLabel = campaign.sent_at
    ? format(new Date(campaign.sent_at), 'd MMM yyyy', { locale: fr })
    : '—';

  return (
    <div className="campaign-overlay-full">
      {/* ── Gradient hero header ── */}
      <div className="cov-hero" style={{ background: heroGradient }}>
        <div className="cov-hero-nebula" />
        <div className="cov-hero-top">
          <div className="cov-hero-left">
            <span className="cov-hero-status-pill">{s.label}</span>
            <h2 className="cov-hero-title">{campaign.name}</h2>
            <div className="cov-hero-meta">
              {campaign.objective && <span>{campaign.objective}</span>}
              {campaign.sent_at && (
                <span>
                  {campaign.status === 'sent'      ? `📅 Envoyée le ${format(new Date(campaign.sent_at), 'd MMMM yyyy', { locale: fr })}` :
                   campaign.status === 'scheduled' ? `📅 Planifiée le ${format(new Date(campaign.sent_at), 'd MMMM yyyy', { locale: fr })}` : ''}
                </span>
              )}
            </div>
          </div>
          <div className="cov-hero-actions">
            {campaign.status === 'draft' && (
              <button className="cov-hero-send-btn" onClick={() => { onSend(campaign.id); onClose(); }}>
                🚀 Envoyer
              </button>
            )}
            <button className="cov-hero-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>
        {/* Partage du mini-rapport (campagne envoyée) — fierté = acquisition */}
        {campaign.status === 'sent' && campaign.share_url && (
          <button
            className="cov-share-btn"
            onClick={() => {
              navigator.clipboard?.writeText(campaign.share_url)
                .then(() => notifyShare('🔗 Lien du rapport copié — montre tes résultats !'))
                .catch(() => window.open(campaign.share_url, '_blank'));
            }}
            title="Copier le lien public de tes résultats (aucune donnée client visible)"
          >
            📤 Partager mes résultats
          </button>
        )}

        {/* Stats inline dans le hero */}
        <div className="cov-hero-stats">
          {[
            { val: campaign.contacts,            label: '👥 Contacts ciblés' },
            { val: campaign.opens,               label: '👁 Ouvertures' },
            { val: `${campaign.open_rate}%`,     label: '📊 Taux ouverture' },
            { val: campaign.clicks || 0,         label: '🔗 Clics' },
            { val: campaign.feedback,            label: '💬 Feedbacks' },
          ].map((st, i) => (
            <div key={i} className="cov-hero-stat">
              <span className="cov-hero-stat-val">{st.val}</span>
              <span className="cov-hero-stat-lbl">{st.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Benchmark bar sous le hero si campagne envoyée ── */}
      {campaign.status === 'sent' && campaign.contacts > 0 && (() => {
        const barColor = rateColor;
        return (
          <div style={{ padding: '14px 28px', background: 'var(--bg)', borderBottom: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
              <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Taux d'ouverture vs moyenne secteur</span>
              <span style={{ color: barColor, fontWeight: 800 }}>{rate}%</span>
            </div>
            <div style={{ position: 'relative', height: 8, background: 'var(--line)', borderRadius: 4 }}>
              <div title="Moyenne secteur 35%" style={{ position: 'absolute', left: '35%', top: -3, bottom: -3, width: 2, background: '#94A3B8', borderRadius: 1, zIndex: 2 }} />
              <div style={{ width: `${Math.min(rate, 100)}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width .4s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 4 }}>
              <span style={{ color: barColor, fontWeight: 600 }}>
                {rate >= 35 ? '✅ Au-dessus de la moyenne' : '⚠️ Sous la moyenne'}
              </span>
              <span style={{ color: '#94A3B8' }}>Moy. secteur 35%</span>
            </div>
          </div>
        );
      })()}

      {/* Tab selector */}
      <div className="campaign-overlay-tabs">
        <button className={`campaign-overlay-tab ${tab === 'journal' ? 'active' : ''}`} onClick={() => setTab('journal')}>
          📋 Journal de bord
          {(campaign.notes || []).length > 0 && (
            <span className="campaign-tab-badge">{(campaign.notes || []).length}</span>
          )}
        </button>
        <button className={`campaign-overlay-tab ${tab === 'rapport' ? 'active' : ''}`} onClick={() => setTab('rapport')}>
          📊 Rapport
          {campaign.report && <span className="campaign-tab-badge" style={{ background: 'var(--success)' }}>✓</span>}
        </button>
      </div>

      {/* Journal de bord */}
      {tab === 'journal' && (
        <div className="campaign-journal">
          {/* Notes list */}
          <div className="campaign-notes-list">
            {(campaign.notes || []).length === 0 ? (
              <div className="campaign-notes-empty">
                <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Aucune note pour l'instant</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>Ajoutez des observations, résultats ou points de suivi ci-dessous.</div>
              </div>
            ) : (
              [...(campaign.notes || [])].reverse().map(note => (
                <div key={note.id} className="campaign-note">
                  <div className="campaign-note-date">{note.date}</div>
                  <div className="note-box" style={{ marginTop: 4 }}>
                    <span className="note-box-icon">📝</span>
                    <span className="note-box-text">{note.text}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add note */}
          <div className="campaign-add-note">
            <div className="campaign-note-compose">
              <div className="campaign-note-compose-icon">📝</div>
              <textarea
                className="campaign-note-input"
                placeholder="Votre note de suivi… résultats, appels reçus, prochaine étape"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddNote(); }}
                rows={2}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)', marginRight: 'auto' }}>⌘+Entrée pour enregistrer</span>
              <Button
                variant="secondary"
                size="sm"
                loading={generatingReport}
                onClick={handleGenerateReport}
              >
                📊 Rapport
              </Button>
              <Button size="sm" disabled={!noteText.trim()} onClick={handleAddNote}>
                + Ajouter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rapport */}
      {tab === 'rapport' && (
        <div className="campaign-rapport">
          {campaign.report ? (
            <>
              {(() => {
                let r = null;
                try { r = JSON.parse(campaign.report); } catch { /* ancien format texte */ }
                if (!r?.score && r?.score !== 0) {
                  return (
                    <div className="campaign-rapport-content">
                      <pre className="campaign-rapport-pre">{campaign.report}</pre>
                    </div>
                  );
                }
                const scoreColor = r.score >= 75 ? '#10B981' : r.score >= 50 ? '#F59E0B' : '#EF4444';
                const C = 2 * Math.PI * 34;
                return (
                  <div className="crapport">
                    {/* Bandeau score + résumé */}
                    <div className="crapport-hero">
                      <div className="crapport-gauge">
                        <svg width="86" height="86" viewBox="0 0 86 86">
                          <circle cx="43" cy="43" r="34" fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="9" />
                          <circle cx="43" cy="43" r="34" fill="none" stroke="#fff" strokeWidth="9" strokeLinecap="round"
                            strokeDasharray={`${C * r.score / 100} ${C}`} transform="rotate(-90 43 43)" />
                        </svg>
                        <span className="crapport-gauge-val">{r.score}</span>
                      </div>
                      <div className="crapport-hero-txt">
                        <div className="crapport-hero-mood">{r.humeur || '📊'} <b>Score de campagne : {r.score}/100</b></div>
                        <p className="crapport-hero-resume">{r.resume}</p>
                      </div>
                    </div>

                    {/* Stats visuelles */}
                    <div className="crapport-bars">
                      {[
                        ['👀 Ouvertures', campaign.open_rate ?? 0, '#10B981'],
                        ['🔗 Clics', campaign.click_rate ?? 0, '#6C5CE7'],
                        ['💛 Feedbacks', campaign.sent_count > 0 ? Math.round((campaign.feedback_count ?? 0) / campaign.sent_count * 100) : 0, '#F59E0B'],
                      ].map(([lbl, pct, col]) => (
                        <div key={lbl} className="crapport-bar-row">
                          <span className="crapport-bar-lbl">{lbl}</span>
                          <div className="crapport-bar-track">
                            <div className="crapport-bar-fill" style={{ width: `${Math.min(100, pct)}%`, background: col }} />
                          </div>
                          <span className="crapport-bar-val" style={{ color: col }}>{pct}%</span>
                        </div>
                      ))}
                    </div>

                    {/* Forces / axes */}
                    <div className="crapport-cols">
                      <div className="crapport-col crapport-col--good">
                        <div className="crapport-col-title">🏆 Ce qui a marché</div>
                        <ul>{(r.points_forts || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
                      </div>
                      <div className="crapport-col crapport-col--warn">
                        <div className="crapport-col-title">🔧 À améliorer</div>
                        <ul>{(r.axes || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
                      </div>
                    </div>

                    {/* 3 recos */}
                    {(r.recos || []).length > 0 && (
                      <div className="crapport-recos">
                        <div className="crapport-recos-title">🚀 Pour ta prochaine campagne</div>
                        {(r.recos || []).map((rec, i) => (
                          <div key={i} className="crapport-reco">
                            <span className="crapport-reco-num">{i + 1}</span>
                            <div>
                              <div className="crapport-reco-t">{rec.titre}</div>
                              <div className="crapport-reco-d">{rec.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Prochaine action */}
                    {r.prochaine_action && (
                      <div className="crapport-next">
                        <span>👉</span>
                        <div><b>Ta prochaine action :</b> {r.prochaine_action}</div>
                        <button className="crapport-next-cta" onClick={() => navigate('/campaigns/new')}>Créer la campagne →</button>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <Button variant="secondary" size="sm" onClick={() => { const w = window.open(); w.document.write(`<pre style="font-family:Arial;padding:24px;font-size:13px;white-space:pre-wrap">${(() => { try { const r = JSON.parse(campaign.report); return `RAPPORT — ${campaign.name}\n\nScore : ${r.score}/100\n\n${r.resume}\n\nPOINTS FORTS\n${(r.points_forts || []).map(p => '• ' + p).join('\n')}\n\nÀ AMÉLIORER\n${(r.axes || []).map(p => '• ' + p).join('\n')}\n\nRECOMMANDATIONS\n${(r.recos || []).map((x, i) => (i + 1) + '. ' + x.titre + ' — ' + x.detail).join('\n')}\n\nPROCHAINE ACTION\n${r.prochaine_action || ''}`; } catch { return campaign.report; } })()}</pre>`); w.print(); }}>
                  🖨 Imprimer
                </Button>
                <Button variant="ghost" size="sm" loading={generatingReport} onClick={handleGenerateReport}>
                  ↺ Régénérer
                </Button>
              </div>
            </>
          ) : (
            <div className="crempty">
              {/* Bandeau récap — la campagne EST partie, on le montre avec fierté */}
              <div className="crempty-head">
                <div className="crempty-head-ic">🚀</div>
                <div className="crempty-head-txt">
                  <div className="crempty-head-title">
                    {campaign.status === 'sent' ? 'Campagne envoyée — les résultats arrivent' : 'Rapport à venir'}
                  </div>
                  <div className="crempty-head-sub">
                    {campaign.status === 'sent'
                      ? "Les premières ouvertures tombent en général dans l'heure. Génère ton rapport complet quand tu veux — je m'occupe de l'analyse."
                      : "Le rapport se génère une fois la campagne envoyée."}
                  </div>
                </div>
              </div>

              {/* Récap aligné de l'envoi */}
              <div className="crempty-grid">
                <div className="crempty-cell">
                  <span className="crempty-cell-lbl">🎯 Objectif</span>
                  <span className="crempty-cell-val">{campaign.objective || '—'}</span>
                </div>
                <div className="crempty-cell">
                  <span className="crempty-cell-lbl">👥 Cible</span>
                  <span className="crempty-cell-val">{(campaign.contacts ?? 0).toLocaleString()} contact{(campaign.contacts ?? 0) > 1 ? 's' : ''}</span>
                </div>
                <div className="crempty-cell">
                  <span className="crempty-cell-lbl">📡 Canal</span>
                  <span className="crempty-cell-val">{channelLabel}</span>
                </div>
                <div className="crempty-cell">
                  <span className="crempty-cell-lbl">📅 Envoi</span>
                  <span className="crempty-cell-val">{sentDateLabel}</span>
                </div>
              </div>

              {/* Ce que le rapport apportera */}
              <div className="crempty-preview">
                <div className="crempty-preview-title">✨ Ton rapport Appee contiendra</div>
                <ul className="crempty-preview-list">
                  <li><b>Un score /100</b> + un résumé clair en une phrase</li>
                  <li><b>Ce qui a marché</b> et <b>ce qu'on améliore</b> pour la prochaine</li>
                  <li><b>3 recommandations</b> concrètes, prêtes à appliquer</li>
                </ul>
              </div>

              <Button loading={generatingReport} onClick={handleGenerateReport} className="crempty-cta">
                ✨ Générer mon rapport Appee
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_ORDER = { sent: 0, sending: 0, scheduled: 1, draft: 2 };

// Packs SMS : source unique partagée (src/data/smsPacks.js) — l'index = celui du checkout
const CAMPAIGN_SMS_PACKS = SMS_PACKS;

// ── Helper : remplace les variables de prévisualisation ──────────────────────
// Sans contact : données d'exemple (Marie Dupont). Avec contact : SES vraies
// données — c'est l'aperçu « ce que recevra exactement cette personne ».
function appendMemoryLine(text, memoryLine) {
  if (!memoryLine || !(memoryLine || '').trim()) return text;
  const hasPs = /\bP\.?\s?S\.?\b/.test(text || '');
  return `${(text || '').trimEnd()}\n\n${hasPs ? 'P.P.S. ' : 'P.S. '}${memoryLine.trim()}`;
}

function applyPreviewVars(text, companyName, contact = null, memoryLine = null) {
  const _age = contactAge(contact?.birth_date);
  const age = _age != null ? String(_age) : (contact ? '' : '42');
  return (text || '')
    .replace(/\{\{prénom\}\}/g, contact ? (contact.first_name || '') : 'Marie')
    .replace(/\{\{nom\}\}/g, contact ? (contact.last_name || '') : 'Dupont')
    .replace(/\{\{entreprise\}\}/g, companyName || 'Votre entreprise')
    .replace(/\{\{ville\}\}/g, contact ? (contact.city || '') : 'Genève')
    .replace(/\{\{âge\}\}/g, age)
    .replace(/\{\{e\}\}/g, contact ? (contact.gender === 'madame' ? 'e' : '') : 'e')
    .replace(/\{\{mémoire\}\}/g, ''); // legacy — le petit mot est ajouté ci-dessous, plus de variable
}

// ── Modal aperçu campagne ────────────────────────────────────────────────────
function CampaignPreviewModal({ campaign, companyName, onClose, contact = null, memoryLine = null }) {
  const { companyProfile } = useApp();
  const channel = campaign.channel || 'email';
  const fill = (t) => applyPreviewVars(t, companyName, contact, memoryLine);
  const fillBody = (t) => appendMemoryLine(fill(t), memoryLine);
  const smsLen  = (campaign.sms_body || '').length;
  const smsMeta = smsInfo(campaign.sms_body || '');

  // Réplique du gabarit WOW serveur (prepareCampaignHtml) — l'aperçu montre la réalité
  const cp = companyProfile || {};
  const isHex = (v) => /^#[0-9a-fA-F]{3,8}$/.test(v || '');
  const brandPrimary   = isHex(cp.brand_primary)   ? cp.brand_primary   : '#5A35FF';
  const brandSecondary = isHex(cp.brand_secondary) ? cp.brand_secondary : '#7C3AED';
  let heroEmoji = '✨';
  try {
    const m = (fill(campaign.subject) || '').match(/(\p{RI}\p{RI}|\p{Extended_Pictographic})/u);
    if (m) heroEmoji = m[0];
  } catch (e) { /* vieux navigateurs : émoji par défaut */ }
  const ctaUrl = ((campaign.body || '').match(/https?:\/\/[^\s<"']+/i) || [null])[0];

  // 🎨 Thème selon l'événement — miroir de EmailService::prepareCampaignHtml
  const EMAIL_THEMES = [
    { kw: ['nationale', '1er août', '1er aout'],       c: ['#D8232A', '#7A0E12'], deco: '🎆 🇨🇭 🎆' },
    { kw: ['noël', 'noel'],                             c: ['#0B5D3B', '#C42842'], deco: '🎄 ✨ 🎁' },
    { kw: ['halloween'],                                c: ['#2A1440', '#F97316'], deco: '🎃 👻 🦇' },
    { kw: ['black friday'],                             c: ['#111827', '#B45309'], deco: '🛍️ ⚡ 🛍️' },
    { kw: ['valentin'],                                 c: ['#BE185D', '#F43F5E'], deco: '❤️ 🌹 ❤️' },
    { kw: ['des mères', 'mamans', 'maman'],             c: ['#DB2777', '#F59E0B'], deco: '💐 💛 💐' },
    { kw: ['des pères', 'papas', 'papa'],               c: ['#1D4ED8', '#0EA5E9'], deco: '💙 🛠 💙' },
    { kw: ['pâques', 'paques'],                         c: ['#7C3AED', '#F59E0B'], deco: '🐣 🌷 🥚' },
    { kw: ['rentrée', 'rentree'],                       c: ['#B45309', '#F59E0B'], deco: '📚 ✏️ 🎒' },
    { kw: ['année', 'annee', 'sylvestre', 'nouvel an'], c: ['#0F172A', '#B45309'], deco: '🎆 🥂 ✨' },
    { kw: ['saint-nicolas', 'nicolas'],                 c: ['#B91C1C', '#7C2D12'], deco: '🎅 🍊 ⭐' },
    { kw: ['femmes'],                                   c: ['#BE185D', '#7C3AED'], deco: '💐 ✨ 💐' },
  ];
  const needle = (fill(campaign.subject) || '').toLowerCase();
  const theme = EMAIL_THEMES.find(t => t.kw.some(kw => needle.includes(kw))) || null;
  const hdrPrimary   = theme ? theme.c[0] : brandPrimary;
  const hdrSecondary = theme ? theme.c[1] : brandSecondary;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="cprev-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cprev-header">
          <div>
            <div className="cprev-title">👁 Aperçu de la campagne</div>
            <div className="cprev-subtitle">{campaign.name}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="cprev-body">

          {/* E-mail preview */}
          {(channel === 'email' || channel === 'email+sms') && (
            <div className="cprev-section">
              {channel === 'email+sms' && <div className="cprev-section-label">📧 E-mail</div>}
              <div className="cprev-email-card">
                <div className="cprev-email-topbar">
                  <span className="cprev-email-from">De : <b>{companyName || 'Votre entreprise'}</b></span>
                  <span className="cprev-email-to">À : Marie Dupont</span>
                </div>
                <div className="cprev-email-subject">
                  {fill(campaign.subject) || <em style={{ color: 'var(--muted)' }}>(Pas d'objet)</em>}
                </div>
                {/* 🎪 Gabarit WOW — réplique fidèle de l'e-mail réellement reçu */}
                <div style={{ background: '#F5F3FB', padding: '18px 12px' }}>
                  <div style={{ maxWidth: 520, margin: '0 auto', background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 10px 30px rgba(40,25,90,.10)' }}>
                    <div style={{ background: `linear-gradient(135deg, ${hdrPrimary}, ${hdrSecondary})`, padding: '24px 22px 20px', textAlign: 'center' }}>
                      {cp.logo_url
                        ? <img src={cp.logo_url} alt={companyName || ''} style={{ maxHeight: 34, maxWidth: 150 }} />
                        : <div style={{ color: 'rgba(255,255,255,.92)', fontWeight: 700, fontSize: 13, letterSpacing: '.04em', textTransform: 'uppercase' }}>{companyName || 'Votre entreprise'}</div>
                      }
                      <div style={{ width: 76, height: 76, lineHeight: '76px', borderRadius: '50%', background: 'rgba(255,255,255,.96)', margin: '12px auto 10px', fontSize: 40, boxShadow: '0 8px 22px rgba(0,0,0,.22)' }}>{heroEmoji}</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', lineHeight: 1.3, maxWidth: 380, margin: '0 auto' }}>
                        {fill(campaign.subject) || ''}
                      </div>
                      {theme && <div style={{ fontSize: 14, letterSpacing: 12, marginTop: 10, opacity: .9 }}>{theme.deco}</div>}
                    </div>
                    <div style={{ padding: '22px 24px 14px', fontSize: 14, lineHeight: 1.7, color: '#2a2340', textAlign: 'left' }}>
                      {(() => {
                        const b = fillBody(campaign.body);
                        if (!b) return <em style={{ color: 'var(--muted)' }}>Aucun contenu</em>;
                        // Miroir du rendu serveur : si le corps contient une balise inline
                        // (image insérée, lien…), on l'affiche en HTML (l'image se montre)
                        // au lieu de la balise en texte ; sinon rendu sûr ligne par ligne.
                        const hasInline = /<(img|a|br|strong|em|b|i|span|p|div|ul|ol|li|table)[\s>/]/i.test(b);
                        if (hasInline) {
                          const html = /<(p|div|h[1-6]|ul|ol|li|table)[\s>/]/i.test(b) ? b : b.replace(/\n/g, '<br>');
                          return <div dangerouslySetInnerHTML={{ __html: html }} />;
                        }
                        return b.split('\n').map((line, i) => (
                          <p key={i} style={{ margin: '0 0 8px' }}>{line || <br />}</p>
                        ));
                      })()}
                      {ctaUrl && (
                        <div style={{ textAlign: 'center', margin: '20px 0 6px' }}>
                          <span style={{ display: 'inline-block', background: brandPrimary, color: '#fff', fontWeight: 700, fontSize: 13, borderRadius: 999, padding: '11px 28px' }}>
                            Découvrir&nbsp;→
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '0 24px 18px', fontSize: 11, color: '#9a93b0', textAlign: 'center' }}>
                      {companyName || 'Votre entreprise'}
                      {cp.city ? ` · ${cp.city}` : ''}
                      {cp.business_phone ? ` · ${cp.business_phone}` : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SMS preview */}
          {(channel === 'sms' || channel === 'email+sms') && (
            <div className="cprev-section">
              {channel === 'email+sms' && <div className="cprev-section-label" style={{ marginTop: 20 }}>📱 SMS</div>}
              <div className="cprev-phone-wrap">
                <div className="cprev-phone">
                  <div className="cprev-phone-notch" />
                  <div className="cprev-phone-screen">
                    <div className="cprev-sms-thread">
                      <div className="cprev-sms-sender">{companyName || 'Votre entreprise'}</div>
                      <div className="cprev-sms-bubble">
                        {fill(campaign.sms_body) || <em style={{ color: '#999' }}>Aucun texte SMS</em>}
                      </div>
                      <div className="cprev-sms-meta">
                        {smsMeta.chars} car. · {smsMeta.segments} SMS{smsMeta.unicode ? ' · emoji' : ''}
                        {smsMeta.segments > 1 && <span style={{ color: '#DC2626', marginLeft: 8 }}>⚠️ {smsMeta.segments} crédits/envoi</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="cprev-footer">
          <div className="cprev-note">
            💡 Aperçu avec données exemple : Marie Dupont · {companyName || 'Votre entreprise'}
          </div>
          <button className="modal-btn-cancel" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ── Campagnes prêtes à l'emploi ─────────────────────────────────────────────
const READY_TEMPLATES = [
  {
    id: 'birthday_month',
    icon: '🎂', color: '#FF4FA3', bg: '#FFF0F7',
    title: 'Anniversaires du mois',
    subtitle: 'Pense à tes clients ce mois-ci',
    desc: "Pour les contacts dont l'anniversaire est ce mois",
    objective: 'Créer une campagne anniversaire',
    filters: { type: 'birthday_month' },
    tone: 'vouvoiement',
    subject: '🎂 Joyeux anniversaire, {{prénom}} !',
    body: `Cher(e) {{prénom}},\n\nToute l'équipe de {{entreprise}} vous souhaite un très joyeux anniversaire !\n\nEn ce jour qui vous appartient, nous tenons à vous dire à quel point votre fidélité nous touche.\n\nAvec toute notre affection,\n{{entreprise}}`,
    sms: `🎂 Joyeux anniversaire {{prénom}} ! Toute l'équipe de {{entreprise}} vous souhaite une belle journée. Merci pour votre fidélité !`,
  },
  {
    id: 'round_age',
    icon: '🎯', color: '#7C3AED', bg: '#F5F3FF',
    title: 'Âge rond',
    subtitle: 'Les 30, 40, 50, 60 ans — moments forts',
    desc: 'Message spécial pour les anniversaires marquants',
    objective: 'Marquer un anniversaire important',
    filters: {},
    tone: 'vouvoiement',
    subject: '🎯 Une étape importante, {{prénom}} — nos vœux sincères',
    body: `Cher(e) {{prénom}},\n\nCertains anniversaires méritent une attention particulière — et le vôtre en fait partie.\n\nEn ce jour exceptionnel, toute l'équipe de {{entreprise}} vous adresse ses plus sincères félicitations.\n\nQue cette nouvelle étape soit riche en belles surprises !\n\nChaleureusement,\n{{entreprise}}`,
    sms: `🎯 Cher(e) {{prénom}}, un anniversaire aussi marquant mérite un message spécial ! Toute l'équipe de {{entreprise}} vous félicite et vous souhaite le meilleur pour cette nouvelle étape.`,
  },
  {
    id: 'vip',
    icon: '⭐', color: '#F59E0B', bg: '#FFFBEB',
    title: 'Programme VIP',
    subtitle: 'Chouchoute tes meilleurs clients',
    desc: 'Pour tes clients les plus engagés',
    objective: 'Remercier les clients fidèles',
    filters: { type: 'vip' },
    tone: 'vouvoiement',
    subject: '⭐ Un message spécial pour vous, {{prénom}}',
    body: `Cher(e) {{prénom}},\n\nVous faites partie des clients qui nous tiennent particulièrement à cœur. Votre fidélité et votre confiance nous touchent profondément.\n\nNous souhaitons vous remercier sincèrement — vous êtes au cœur de ce que nous faisons.\n\nÀ très bientôt,\n{{entreprise}}`,
    sms: `⭐ Cher(e) {{prénom}}, merci pour votre fidélité exceptionnelle ! Vous comptez vraiment pour nous. — {{entreprise}}`,
  },
  {
    id: 'dormant',
    icon: '💤', color: '#7B61FF', bg: '#EDE9FF',
    title: 'Clients dormants',
    subtitle: 'Recrée le lien avec douceur',
    desc: 'Pour les contacts sans interaction depuis 12 mois+',
    objective: 'Réactiver les clients dormants',
    filters: { type: 'dormant' },
    tone: 'vouvoiement',
    subject: '{{prénom}}, vous nous manquez 💙',
    body: `Bonjour {{prénom}},\n\nCela fait un moment que nous n'avons pas eu de nouvelles l'un de l'autre. Nous espérons que vous allez bien.\n\nNous voulions simplement vous dire que vous comptez pour nous — et que notre porte vous est toujours ouverte.\n\nChaleureusement,\n{{entreprise}}`,
    sms: `Bonjour {{prénom}}, vous nous manquez ! N'hésitez pas à revenir nous voir. — {{entreprise}}`,
  },
  {
    id: 'merci',
    icon: '🙏', color: '#10B981', bg: '#ECFDF5',
    title: 'Remerciements fidèles',
    subtitle: 'Dis merci à tes clients fidèles',
    desc: 'Pour renforcer la relation sur le long terme',
    objective: 'Fidéliser mes clients',
    filters: {},
    tone: 'vouvoiement',
    subject: '🙏 Merci pour votre confiance, {{prénom}}',
    body: `Cher(e) {{prénom}},\n\nNous voulions simplement prendre un moment pour vous dire merci.\n\nMerci de nous faire confiance. Merci de nous choisir. Votre fidélité nous motive chaque jour à donner le meilleur de nous-mêmes.\n\nAvec toute notre reconnaissance,\n{{entreprise}}`,
    sms: `🙏 {{prénom}}, un grand merci pour votre fidélité ! Nous sommes heureux de vous compter parmi nos clients. — {{entreprise}}`,
  },
  {
    id: 'promo',
    icon: '🎁', color: '#EF4444', bg: '#FEF2F2',
    title: 'Offre ou promotion',
    subtitle: 'Lance une offre ciblée',
    desc: 'Pour tout ou partie de ta base active',
    objective: 'Lancer une offre ou une promotion',
    filters: {},
    tone: 'vouvoiement',
    subject: '🎁 Une offre exclusive pour vous, {{prénom}}',
    body: `Bonjour {{prénom}},\n\nNous avons une excellente nouvelle : une offre spéciale pensée pour nos clients fidèles.\n\n[Décrivez votre offre ici]\n\nCette offre est valable pour une durée limitée.\n\nÀ très bientôt,\n{{entreprise}}`,
    sms: `🎁 {{prénom}}, offre exclusive pour vous ! [Votre offre ici]. Valable jusqu'au [date]. — {{entreprise}}`,
  },
  {
    id: 'local',
    icon: '📍', color: '#0EA5E9', bg: '#F0F9FF',
    title: 'Campagne locale',
    subtitle: 'Cible par ville ou région',
    desc: 'Pour tes contacts dans une zone géographique',
    objective: 'Campagne locale ciblée',
    filters: {},
    tone: 'vouvoiement',
    subject: '📍 Un message depuis {{ville}}, {{prénom}}',
    body: `Bonjour {{prénom}},\n\nNous sommes heureux de vous retrouver, vous qui êtes dans notre région.\n\n[Votre message local ici — événement, ouverture, actualité…]\n\nÀ bientôt près de chez vous,\n{{entreprise}}`,
    sms: `📍 Bonjour {{prénom}} ! [Votre actualité locale]. À bientôt — {{entreprise}}`,
  },
  {
    id: 'pense_a_toi',
    icon: '💌', color: '#EC4899', bg: '#FDF2F8',
    title: 'On pense à vous',
    subtitle: 'Une attention sans occasion précise',
    desc: 'Message de lien spontané pour rester présent',
    objective: 'Maintenir la relation client',
    filters: {},
    tone: 'vouvoiement',
    subject: '💌 Une pensée pour vous, {{prénom}}',
    body: `Bonjour {{prénom}},\n\nPas d'occasion particulière aujourd'hui — nous voulions simplement vous faire signe.\n\nVous pensez à nous pour vos prochains besoins ? Nous sommes là, comme toujours.\n\nBelle journée,\n{{entreprise}}`,
    sms: `💌 Bonjour {{prénom}}, on pensait à vous aujourd'hui ! N'hésitez pas si besoin. — {{entreprise}}`,
  },
  {
    id: 'apres_vente',
    icon: '🤝', color: '#6366F1', bg: '#EEF2FF',
    title: 'Après-vente / Suivi',
    subtitle: 'Vérifier la satisfaction après un achat',
    desc: 'Pour tes clients récents',
    objective: 'Suivi satisfaction client',
    filters: {},
    tone: 'vouvoiement',
    subject: '🤝 Comment s\'est passé votre expérience, {{prénom}} ?',
    body: `Bonjour {{prénom}},\n\nNous espérons que votre récente expérience avec {{entreprise}} vous a entièrement satisfait(e).\n\nVotre avis compte beaucoup pour nous. Si vous avez le moindre retour — positif ou à améliorer — nous serions ravis de l'entendre.\n\nMerci pour votre confiance,\n{{entreprise}}`,
    sms: `🤝 {{prénom}}, comment s'est passée votre expérience ? Votre avis compte pour nous ! — {{entreprise}}`,
  },
  {
    id: 'invitation',
    icon: '🎪', color: '#8B5CF6', bg: '#F5F3FF',
    title: 'Invitation événement',
    subtitle: 'Convie tes clients à un événement',
    desc: 'Portes ouvertes, lancement, soirée, atelier…',
    objective: 'Inviter mes clients à un événement',
    filters: {},
    tone: 'vouvoiement',
    subject: '🎪 Vous êtes invité(e), {{prénom}} !',
    body: `Cher(e) {{prénom}},\n\nNous avons le plaisir de vous convier à [nom de l'événement] le [date] à [lieu].\n\nCet événement est pensé pour vous — nos clients que nous apprécions particulièrement.\n\nNous serions ravis de vous y retrouver.\n\nÀ bientôt,\n{{entreprise}}`,
    sms: `🎪 {{prénom}}, vous êtes invité(e) ! [Événement] le [date] à [lieu]. — {{entreprise}}`,
  },
  {
    id: 'noel',
    icon: '🎄', color: '#DC2626', bg: '#FEF2F2',
    title: 'Vœux de Noël',
    subtitle: "Termine l'année sur une belle note",
    desc: 'Pour toute ta base active',
    objective: "Vœux de fin d'année",
    filters: {},
    tone: 'vouvoiement',
    seasonal: true,
    subject: '🎄 Joyeux Noël, {{prénom}} — nos meilleurs vœux',
    body: `Cher(e) {{prénom}},\n\nÀ l'approche des fêtes, toute notre équipe vous adresse ses vœux les plus chaleureux.\n\nMerci pour votre confiance et votre fidélité tout au long de cette année.\n\nJoyeuses fêtes,\n{{entreprise}}`,
    sms: `🎄 Joyeux Noël {{prénom}} ! Toute l'équipe de {{entreprise}} vous souhaite de belles fêtes et vous remercie pour votre fidélité.`,
  },
  {
    id: 'valentin',
    icon: '💝', color: '#DB2777', bg: '#FDF2F8',
    title: 'Saint-Valentin',
    subtitle: 'Une attention chaleureuse',
    desc: 'Pour tes clients actifs',
    objective: 'Saint-Valentin — message chaleureux',
    filters: {},
    tone: 'vouvoiement',
    seasonal: true,
    subject: '💝 Une pensée pour vous en ce 14 février, {{prénom}}',
    body: `Cher(e) {{prénom}},\n\nEn ce jour dédié aux personnes qui comptent, nous tenions à vous envoyer un message.\n\nVotre fidélité nous touche sincèrement.\n\nAvec notre plus sincère affection,\n{{entreprise}}`,
    sms: `💝 Bonne Saint-Valentin {{prénom}} ! Une pensée de toute l'équipe {{entreprise}} pour vous qui comptez pour nous. ❤️`,
  },
  {
    id: 'renew',
    icon: '🥂', color: '#7C3AED', bg: '#F5F3FF',
    title: 'Bonne année',
    subtitle: "Commence l'année en force",
    desc: "Vœux du Nouvel An pour ta base active",
    objective: 'Bonne année — vœux Nouvel An',
    filters: {},
    tone: 'vouvoiement',
    seasonal: true,
    subject: '🥂 Bonne année {{prénom}} — nos vœux pour cette nouvelle année',
    body: `Cher(e) {{prénom}},\n\nEn ce début d'année, toute l'équipe de {{entreprise}} vous souhaite une excellente année — pleine de santé, de succès et de beaux projets.\n\nMeilleurs vœux,\n{{entreprise}}`,
    sms: `🥂 Bonne année {{prénom}} ! Santé, bonheur et succès pour cette nouvelle année. — {{entreprise}}`,
  },
];

// ── Catégories de templates ──────────────────────────────────────────────────
const BASE_CATEGORIES = [
  { id: 'anniversaire', icon: '🎂', label: 'Anniversaires',      color: '#FF4FA3', ids: ['birthday_month', 'round_age'] },
  { id: 'fidelite',     icon: '💙', label: 'Fidélité & relation', color: '#5A35FF', ids: ['vip', 'merci', 'dormant', 'pense_a_toi', 'apres_vente'] },
  { id: 'action',       icon: '📣', label: 'Offres & événements', color: '#10B981', ids: ['promo', 'local', 'invitation'] },
  { id: 'saisonniere',  icon: '🗓', label: 'Saisonnières',        color: '#F59E0B', ids: ['noel', 'valentin', 'renew'] },
];

const CUSTOM_CATEGORY = { id: 'custom', icon: '✨', label: 'Mes modèles', color: '#7C4DFF' };

// Conversion template API ↔ format local
function apiToCustom(t) {
  const m = t.metadata || {};
  return {
    id: String(t.id),
    _apiId: t.id,
    icon: m.icon || '📧',
    title: t.name,
    desc: m.desc || '',
    category: m.category || 'custom',
    audience: m.audience || '',
    channel: m.channel || 'email',
    objective: m.objective || t.name,
    subject: t.subject || '',
    body: t.body || '',
    imageUrl: m.imageUrl || '',
    custom: true,
  };
}
function customToApi(tpl) {
  return {
    name: tpl.title,
    type: 'custom',
    subject: tpl.subject || '',
    body: tpl.body || '',
    metadata: {
      type: 'custom_campaign',
      icon: tpl.icon,
      desc: tpl.desc,
      category: tpl.category,
      audience: tpl.audience,
      channel: tpl.channel,
      objective: tpl.objective,
      imageUrl: tpl.imageUrl,
    },
  };
}

// ── Modal création modèle perso ───────────────────────────────────────────────
const CAT_OPTIONS = [
  ...BASE_CATEGORIES.map(c => ({ value: c.id, label: `${c.icon} ${c.label}` })),
  { value: 'custom', label: '✨ Mes modèles' },
];

function CustomTemplateModal({ onSave, onClose, initial }) {
  const [form, setForm] = useState(initial || {
    icon: '📧', title: '', desc: '', category: 'custom',
    subject: '', body: '',
  });
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.title.trim()) { setError('Donne un nom à ce modèle.'); return; }
    if (!form.subject.trim()) { setError('Ajoute un objet d\'e-mail.'); return; }
    onSave({ ...form, id: initial?.id || `custom_${Date.now()}`, custom: true });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{initial ? 'Modifier le modèle' : 'Nouveau modèle perso'}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">

          {/* Emoji + Nom */}
          <div className="modal-field-row">
            <div className="modal-field" style={{ width: 72, flexShrink: 0 }}>
              <label className="modal-label">Icône</label>
              <input
                className="modal-input modal-input-emoji"
                value={form.icon}
                onChange={e => set('icon', e.target.value)}
                maxLength={2}
                placeholder="📧"
              />
            </div>
            <div className="modal-field" style={{ flex: 1 }}>
              <label className="modal-label">Nom du modèle *</label>
              <input
                className="modal-input"
                value={form.title}
                onChange={e => set('title', e.target.value)}
                placeholder="ex: Fête des Pères"
              />
            </div>
          </div>

          {/* Catégorie */}
          <div className="modal-field">
            <label className="modal-label">Catégorie</label>
            <select className="modal-input" value={form.category} onChange={e => set('category', e.target.value)}>
              {CAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Description */}
          <div className="modal-field">
            <label className="modal-label">Description courte</label>
            <input
              className="modal-input"
              value={form.desc}
              onChange={e => set('desc', e.target.value)}
              placeholder="ex: Pour tous tes clients pères de famille"
            />
          </div>

          {/* Objet */}
          <div className="modal-field">
            <label className="modal-label">Objet de l'e-mail *</label>
            <input
              className="modal-input"
              value={form.subject}
              onChange={e => set('subject', e.target.value)}
              placeholder="ex: 👔 Bonne fête des Pères, {{prénom}} !"
            />
          </div>

          {/* Corps */}
          <div className="modal-field">
            <label className="modal-label">Corps du message</label>
            <textarea
              className="modal-input modal-textarea"
              value={form.body}
              onChange={e => set('body', e.target.value)}
              placeholder={"Bonjour {{prénom}},\n\nÀ l'occasion de la fête des Pères…\n\n{{entreprise}}"}
              rows={5}
            />
            <div className="modal-hint">Variables disponibles : {'{{prénom}}'}, {'{{entreprise}}'}, {'{{ville}}'}</div>
          </div>

          {error && <div className="modal-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="modal-btn-cancel" onClick={onClose}>Annuler</button>
          <button className="modal-btn-save" onClick={handleSave}>
            {initial ? 'Enregistrer' : '+ Créer le modèle'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadyCampaigns({ contacts }) {
  const navigate = useNavigate();
  const [openCat, setOpenCat]     = useState('anniversaire');
  const [customList, setCustomList] = useState([]);
  const [showModal, setShowModal]  = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Charger les modèles perso depuis l'API au montage
  useEffect(() => {
    api.getTemplates()
      .then(tpls => {
        const customs = (tpls || [])
          .filter(t => t.metadata?.type === 'custom_campaign')
          .map(apiToCustom);
        setCustomList(customs);
      })
      .catch(() => {
        // Fallback : localStorage si l'API échoue
        try { setCustomList(JSON.parse(localStorage.getItem('appee_custom_templates') || '[]')); } catch {}
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalActive = contacts.filter(c => c.status === 'active').length;

  // Catégories avec onglet "Mes modèles" si au moins 1 modèle perso
  const allCategories = customList.length > 0
    ? [...BASE_CATEGORIES, CUSTOM_CATEGORY]
    : BASE_CATEGORIES;

  const saveCustom = async (tpl) => {
    try {
      if (tpl._apiId) {
        // Mise à jour
        const saved = await api.updateTemplate(tpl._apiId, customToApi(tpl));
        setCustomList(prev => prev.map(t => t._apiId === tpl._apiId ? apiToCustom(saved) : t));
      } else {
        // Création
        const saved = await api.createTemplate(customToApi(tpl));
        setCustomList(prev => [...prev, apiToCustom(saved)]);
      }
    } catch {
      // Fallback local si API indisponible
      setCustomList(prev => {
        const idx = prev.findIndex(t => t.id === tpl.id);
        return idx >= 0 ? prev.map(t => t.id === tpl.id ? tpl : t) : [...prev, tpl];
      });
    }
    setShowModal(false);
    setEditTarget(null);
    setOpenCat(tpl.category || 'custom');
  };

  const deleteCustom = async (id) => {
    const tpl = customList.find(t => t.id === id);
    setCustomList(prev => {
      const next = prev.filter(t => t.id !== id);
      if (next.filter(t => t.category === openCat || openCat === 'custom').length === 0 && openCat === 'custom') {
        setOpenCat('anniversaire');
      }
      return next;
    });
    if (tpl?._apiId) {
      try { await api.deleteTemplate(tpl._apiId); } catch {}
    }
  };

  const launchTemplate = (t) => navigate('/campaigns/new', {
    state: {
      startStep: 2,
      preset: {
        objective: t.objective || t.title,
        filters:   t.filters || {},
        tone:      t.tone || 'vouvoiement',
        name:      t.title,
        subject:   t.subject,
        body:      t.body || '',
        sms:       t.sms || '',
      }
    }
  });

  // Items à afficher pour la catégorie active
  const getItems = (catId) => {
    const base = READY_TEMPLATES.filter(t => {
      const cat = BASE_CATEGORIES.find(c => c.id === catId);
      return cat?.ids.includes(t.id);
    });
    const customs = customList.filter(t =>
      catId === 'custom' ? true : t.category === catId
    );
    return [...base, ...customs];
  };

  return (
    <div className="ready-section">
      <div className="ready-header">
        <div>
          <div className="ready-title">✉️ Modèles prêts à envoyer</div>
          <div className="ready-sub">Je prépare le message, la cible et l'objet — toi tu valides en un clic.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {totalActive > 0 && <div className="ready-base-pill">{totalActive} contacts actifs</div>}
          <button className="ready-add-btn" onClick={() => { setEditTarget(null); setShowModal(true); }} title="Créer un modèle perso">
            + Ajouter
          </button>
        </div>
      </div>

      {/* Onglets catégories */}
      <div className="ready-tabs">
        {allCategories.map(cat => (
          <button
            key={cat.id}
            className={`ready-tab ${openCat === cat.id ? 'active' : ''}`}
            style={{ '--cat-color': cat.color }}
            onClick={() => setOpenCat(cat.id)}
          >
            <span>{cat.icon}</span> {cat.label}
            {cat.id === 'custom' && customList.length > 0 && (
              <span className="ready-tab-count">{customList.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Grille de cartes */}
      <div className="ready-grid">
        {getItems(openCat).map(t => (
          <div key={t.id} className="ready-card" style={{ '--rc-color': t.color || '#7C4DFF', '--rc-bg': t.bg || '#F5F3FF' }}>
            <div className="ready-card-top">
              <div className="ready-card-header">
                <div className="ready-card-icon">{t.icon}</div>
                <div className="ready-card-title">
                  {t.title}
                  {t.custom && <span className="ready-custom-badge">perso</span>}
                </div>
              </div>
              <div className="ready-card-subtitle">{t.subtitle}</div>
              <div className="ready-card-why">{t.desc}</div>
            </div>
            <div className="ready-card-footer">
              <button className="ready-card-cta" onClick={() => launchTemplate(t)}>
                Utiliser →
              </button>
              {t.custom && (
                <div className="ready-card-actions">
                  <button className="ready-row-edit" onClick={() => { setEditTarget(t); setShowModal(true); }} title="Modifier">✏️</button>
                  <button className="ready-row-del" onClick={() => setConfirmDeleteId(t.id)} title="Supprimer">🗑</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {getItems(openCat).length === 0 && (
          <div className="ready-empty-grid">
            <span>Aucun modèle dans cette catégorie.</span>
            <button className="ready-empty-add" onClick={() => { setEditTarget(null); setShowModal(true); }}>+ Créer le premier</button>
          </div>
        )}
      </div>

      {showModal && (
        <CustomTemplateModal
          initial={editTarget}
          onSave={saveCustom}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
        />
      )}

      <ConfirmModal
        open={!!confirmDeleteId}
        title="Supprimer ce modèle ?"
        message="Ce modèle personnalisé sera supprimé définitivement."
        confirmLabel="Oui, supprimer"
        danger
        onConfirm={() => { deleteCustom(confirmDeleteId); setConfirmDeleteId(null); }}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}

/* ── CmpStat card ── */
function CmpStat({ icon, label, value, suffix = '', color, delay = 0 }) {
  const count = useCountUp(value, 900);
  return (
    <div className="cmp-stat-card" style={{ animationDelay: `${delay}ms` }}>
      <div className="cmp-stat-icon" style={{ background: color }}>{icon}</div>
      <div className="cmp-stat-body">
        <div className="cmp-stat-value">{count.toLocaleString()}{suffix}</div>
        <div className="cmp-stat-label">{label}</div>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const { campaigns, setCampaigns, addNotification, contacts, tenant } = useApp();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deletingCamp, setDeletingCamp] = useState(false);

  const selectedCampaign = campaigns.find(c => c.id === selectedId) || null;

  const doDeleteCampaign = async () => {
    if (!confirmDel) return;
    const c = confirmDel;
    setDeletingCamp(true);
    try {
      await api.deleteCampaign(c.id);
      setCampaigns(cs => cs.filter(x => x.id !== c.id));
      addNotification(`Campagne « ${c.name} » supprimée.`);
      setConfirmDel(null);
    } catch {
      addNotification('Impossible de supprimer cette campagne. Réessaie.', 'error');
    } finally {
      setDeletingCamp(false);
    }
  };

  // Refresh stats when opening a sent campaign
  const handleSelectCampaign = async (id) => {
    setSelectedId(id);
    const camp = campaigns.find(c => c.id === id);
    if (camp?.status === 'sent') {
      try {
        const res = await api.campaignStats(id);
        const updated = {
          ...camp,
          opens:     res.opened_count    ?? res.campaign?.opened_count    ?? camp.opens,
          clicks:    res.clicked_count   ?? res.campaign?.clicked_count   ?? camp.clicks,
          feedback:  res.feedback_count  ?? res.campaign?.feedback_count  ?? camp.feedback,
          open_rate: res.open_rate       ?? res.campaign?.open_rate       ?? camp.open_rate,
          contacts:  res.sent_count      ?? res.campaign?.sent_count      ?? camp.contacts,
          share_url: res.share_url       ?? camp.share_url,
        };
        setCampaigns(cs => cs.map(c => c.id === id ? updated : c));
      } catch (e) { /* silent */ }
    }
  };

  // Sort: sent most-recent-first, then scheduled, then drafts
  const sorted = [...campaigns].sort((a, b) => {
    const so = (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3);
    if (so !== 0) return so;
    if (a.sent_at && b.sent_at) return new Date(b.sent_at) - new Date(a.sent_at);
    return b.id - a.id;
  });

  const handleSend = async (id) => {
    try {
      await api.sendCampaign(id);
      setCampaigns(cs => cs.map(c => c.id === id
        ? { ...c, status: 'sent', sent_at: localDateStr() }
        : c
      ));
      addNotification('🚀 Campagne envoyée avec succès !');
    } catch (e) {
      addNotification('Erreur envoi : ' + (e.message || 'Impossible d\'envoyer la campagne'), 'error');
    }
  };

  const handleAddNote = (id, note) => {
    setCampaigns(cs => cs.map(c => c.id === id
      ? { ...c, notes: [...(c.notes || []), note] }
      : c
    ));
  };

  const handleUpdateReport = (id, report) => {
    setCampaigns(cs => cs.map(c => c.id === id ? { ...c, report } : c));
    addNotification('📊 Rapport généré avec succès !');
  };

  const groups = [
    { key: 'sent',      label: '✅ En cours / Envoyées',  items: sorted.filter(c => c.status === 'sent' || c.status === 'sending') },
    { key: 'scheduled', label: '📅 Programmées',           items: sorted.filter(c => c.status === 'scheduled') },
    { key: 'draft',     label: '✏️ Brouillons',            items: sorted.filter(c => c.status === 'draft') },
  ].filter(g => g.items.length > 0);

  const renderCard = (c, idx = 0) => {
    const s = statusLabels[c.status] || statusLabels.draft;
    const notes = c.notes || [];
    const channelIcon = c.channel === 'sms' ? '📱' : c.channel === 'email+sms' ? '🚀' : '📧';
    return (
      <div key={c.id} className="camp-row" style={{ animationDelay: `${idx * 40}ms` }}>
        <div className="camp-row-left">
          <div className="camp-row-channel-badge" title={c.channel || 'email'}>{channelIcon}</div>
          <div className="camp-row-info">
            <span className="camp-row-name">{c.name}</span>
            {c.objective && <span className="camp-row-obj">{c.objective}</span>}
          </div>
        </div>
        <div className="camp-row-stats">
          {(c.email_sent_count != null || c.sms_sent_count != null) ? (
            <>
              {c.email_sent_count > 0 && <span className="cmp-stat-pill cmp-stat-pill-email">📧 <b>{c.email_sent_count}</b></span>}
              {c.sms_sent_count > 0 && <span className="cmp-stat-pill cmp-stat-pill-sms">📱 <b>{c.sms_sent_count}</b></span>}
            </>
          ) : (
            <span className="cmp-stat-pill"><b>{c.contacts || 0}</b> contacts</span>
          )}
          {c.status === 'sent' && c.open_rate > 0 && (
            <span className={`cmp-stat-pill cmp-stat-pill-open${c.open_rate >= 35 ? ' cmp-open-good' : c.open_rate < 15 ? ' cmp-open-low' : ''}`}>
              <span className="cmp-open-dot" />
              <b>{c.open_rate}%</b> ouv.
            </span>
          )}
          {c.sent_at && <span className="camp-row-date">{format(new Date(c.sent_at), 'd MMM yyyy', { locale: fr })}</span>}
        </div>
        <div className="camp-row-right">
          <span className={`cmp-status-badge cmp-status-${c.status}`}>{s.label}</span>
          {c.status === 'sent' && c.share_url && (
            <button
              className="camp-row-btn camp-row-share"
              title="Copier le lien public de tes résultats"
              onClick={() => {
                navigator.clipboard?.writeText(c.share_url)
                  .then(() => addNotification('🔗 Lien du rapport copié — montre tes résultats !'))
                  .catch(() => window.open(c.share_url, '_blank'));
              }}
            >📤</button>
          )}
          <button className="camp-row-btn" onClick={() => handleSelectCampaign(c.id)}>
            {notes.length > 0 && <span className="camp-row-notif">{notes.length}</span>}
            Détails
          </button>
          {c.status === 'draft' && (
            <>
              <button className="camp-row-preview" onClick={() => setPreviewCampaign(c)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                Aperçu
              </button>
              <button className="camp-row-edit" onClick={() => navigate('/campaigns/new', { state: { editId: c.id, preset: { name: c.name, objective: c.objective, subject: c.subject, body: c.body, sms: c.sms_body, tone: c.tone || 'vouvoiement', channel: c.channel || 'email', filters: c.segment_filters || {} } } })}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Modifier
              </button>
              <button className="camp-row-send" onClick={() => handleSend(c.id)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9l20-7z"/></svg>
                Envoyer
              </button>
            </>
          )}
          {c.status === 'sending' && (
            /* Envoi figé (timeout) : le backend reprend là où il s'est arrêté,
               zéro doublon (idempotence par contact). S'il tourne encore, il
               répond gentiment « patiente ». */
            <button className="camp-row-send" onClick={() => handleSend(c.id)} title="Reprendre l'envoi là où il s'est arrêté — aucun contact ne recevra deux fois">
              🔄 Reprendre l'envoi
            </button>
          )}
          <button
            className="camp-row-del"
            onClick={() => setConfirmDel(c)}
            title="Supprimer définitivement cette campagne"
            aria-label="Supprimer la campagne"
          >🗑</button>
        </div>
      </div>
    );
  };

  const sentCount   = campaigns.filter(c => c.status === 'sent').length;
  const draftCount  = campaigns.filter(c => c.status === 'draft').length;
  const schedCount  = campaigns.filter(c => c.status === 'scheduled').length;
  const sentWithRate = campaigns.filter(c => c.status === 'sent' && Number(c.open_rate) > 0);
  const avgOpenRate = sentWithRate.length > 0
    ? Math.round(sentWithRate.reduce((s, c) => s + Number(c.open_rate), 0) / sentWithRate.length)
    : 0;

  return (
    <div className="campaigns-page">

      {/* ── Hero ── */}
      <div className="cmp-hero cmp-animate-in">
        <div className="cmp-hero-nebula" />
        <div className="cmp-hero-top">
          <div className="cmp-hero-left">
            <div className="cmp-hero-icon-wrap">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13"/>
                <path d="M22 2 15 22 11 13 2 9l20-7z"/>
              </svg>
            </div>
            <div>
              <h1 className="cmp-hero-title">Campagnes</h1>
              <p className="cmp-hero-sub">{campaigns.length} campagne{campaigns.length > 1 ? 's' : ''} créée{campaigns.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="cmp-hero-actions">
            <Link to="/campaigns/new">
              <button className="cmp-btn-primary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Nouvelle campagne
              </button>
            </Link>
          </div>
        </div>

        <SectionHint>
          Une campagne = un message (e-mail ou SMS) envoyé à un groupe de clients ciblés. Appee <strong>rédige et cible pour toi</strong> ; tu valides, ça part. C'est ton canal pour vendre et fidéliser.
        </SectionHint>

        <GenieTip
          id="campaigns"
          title="📣 Tes campagnes"
          foot={<>Clique « Nouvelle campagne » : choisis un objectif, Appee cible les bons contacts et rédige le texte.</>}
        >
          Envoie un message à tout un groupe d'un coup — e-mail ou SMS. Idéal pour une promo, une réactivation ou une annonce.
        </GenieTip>

        {/* Stats */}
        <div className="cmp-stats-row">
          <CmpStat icon="📢" label="Total" value={campaigns.length} color="rgba(90,53,255,.25)" delay={0} />
          <CmpStat icon="✅" label="Envoyées" value={sentCount} color="rgba(16,185,129,.25)" delay={60} />
          <CmpStat icon="📅" label="Programmées" value={schedCount} color="rgba(59,130,246,.25)" delay={120} />
          <CmpStat icon="✏️" label="Brouillons" value={draftCount} color="rgba(107,114,128,.2)" delay={180} />
          {sentWithRate.length > 0 && (
            <CmpStat icon="📬" label="Taux d'ouverture" value={avgOpenRate} suffix="%" color="rgba(245,158,11,.25)" delay={240} />
          )}
        </div>

        {/* ── Insight contextuel ── */}
        {campaigns.length > 0 && (() => {
          if (draftCount > 0)
            return (
              <div className="cmp-insight">
                <span className="cmp-insight-icon">✏️</span>
                <span>{draftCount} brouillon{draftCount > 1 ? 's' : ''} en attente — envoie-{draftCount > 1 ? 'les' : 'le'} pour maximiser ton impact.</span>
                <Link to="/campaigns/new" className="cmp-insight-cta">Voir les brouillons →</Link>
              </div>
            );
          if (avgOpenRate > 0 && avgOpenRate < 20)
            return (
              <div className="cmp-insight">
                <span className="cmp-insight-icon">💡</span>
                <span>Taux d'ouverture de <strong>{avgOpenRate} %</strong> — essaie un objet plus personnalisé ou un envoi en milieu de semaine.</span>
              </div>
            );
          if (avgOpenRate >= 40)
            return (
              <div className="cmp-insight cmp-insight-success">
                <span className="cmp-insight-icon">🏆</span>
                <span>Excellent taux d'ouverture de <strong>{avgOpenRate} %</strong> — la moyenne est ~25 %. Continue !</span>
              </div>
            );
          if (sentCount >= 1 && sentCount <= 2)
            return (
              <div className="cmp-insight">
                <span className="cmp-insight-icon">👏</span>
                <span>Bonne start ! Pour gagner en régularité, lance une stratégie automatisée.</span>
                <Link to="/strategy" className="cmp-insight-cta">Découvrir les stratégies →</Link>
              </div>
            );
          return null;
        })()}
      </div>

      {/* ── Liste campagnes ── */}
      <div className="campaigns-list cmp-animate-in" style={{ animationDelay: '80ms' }}>
        {groups.length === 0 ? (
          <div className="camp-empty-teaser">
            <div className="camp-empty-head">
              <span className="camp-empty-badge">⚡ Prêt à envoyer</span>
              <h2 className="camp-empty-title">Ta première campagne en moins de 3 minutes</h2>
              <p className="camp-empty-sub">L'IA génère l'objet, le message et choisit la bonne cible — toi tu valides et tu envoies.</p>
            </div>

            <div className="camp-empty-preview">
              {[
                { icon: '💛', title: 'Fidéliser mes clients',        color: '#D97706', bg: '#FFFBEB', result: '~35 % de taux d\'ouverture' },
                { icon: '🎂', title: 'Campagne anniversaire',         color: '#DB2777', bg: '#FFF0F7', result: '5× plus engageant' },
                { icon: '💤', title: 'Réactiver les dormants',        color: '#7B61FF', bg: '#F5F3FF', result: 'Récupère 20 % des inactifs' },
                { icon: '🎯', title: 'Lancer une offre ou une promo', color: '#DC2626', bg: '#FEF2F2', result: 'Augmente les visites' },
              ].map(obj => (
                <div key={obj.title} className="camp-empty-card" style={{ '--ocolor': obj.color, '--obg': obj.bg }}>
                  <div className="camp-empty-card-icon">{obj.icon}</div>
                  <div className="camp-empty-card-title">{obj.title}</div>
                  <div className="camp-empty-card-result">{obj.result}</div>
                  <div className="camp-empty-card-bar" />
                </div>
              ))}
            </div>

            <div className="camp-empty-stats">
              <div className="camp-empty-stat"><span>📬</span><strong>42 %</strong><span>taux d'ouverture moyen</span></div>
              <div className="camp-empty-stat-sep" />
              <div className="camp-empty-stat"><span>⚡</span><strong>3 min</strong><span>pour créer une campagne</span></div>
              <div className="camp-empty-stat-sep" />
              <div className="camp-empty-stat"><span>🎯</span><strong>10+</strong><span>objectifs disponibles</span></div>
            </div>

            <Link to="/campaigns/new" className="camp-launch-btn">
              Créer ma première campagne →
            </Link>
          </div>
        ) : groups.map(g => {
          const withRate = g.items.filter(c => Number(c.open_rate) > 0);
          const groupAvg = g.key === 'sent' && withRate.length > 0
            ? Math.round(withRate.reduce((s, c) => s + Number(c.open_rate), 0) / withRate.length)
            : null;
          return (
          <div key={g.key} className="campaign-group">
            <div className="campaign-group-title">
              <span className="cmp-group-dot" data-key={g.key} />
              {g.label}
              <span className="campaign-group-count">{g.items.length}</span>
              {groupAvg !== null && (
                <span className={`cmp-group-avg-rate${groupAvg >= 35 ? ' cmp-group-avg-good' : groupAvg < 15 ? ' cmp-group-avg-low' : ''}`}>
                  Ø {groupAvg}% ouv.
                </span>
              )}
            </div>
            <div className="cmp-group-card">
              {g.items.map((c, idx) => renderCard(c, idx))}
            </div>
          </div>
          );
        })}
      </div>

      {selectedCampaign && (
        <CampaignDetailOverlay
          campaign={selectedCampaign}
          onClose={() => setSelectedId(null)}
          onSend={handleSend}
          onAddNote={handleAddNote}
          onUpdateReport={handleUpdateReport}
        />
      )}

      {previewCampaign && (
        <CampaignPreviewModal
          campaign={previewCampaign}
          companyName={tenant?.name}
          onClose={() => setPreviewCampaign(null)}
        />
      )}

      {confirmDel && createPortal(
        <div className="camp-del-overlay" onClick={() => !deletingCamp && setConfirmDel(null)}>
          <div className="camp-del-modal" onClick={e => e.stopPropagation()}>
            <div className="camp-del-icon">🗑</div>
            <h3 className="camp-del-title">Supprimer cette campagne ?</h3>
            <p className="camp-del-txt">
              « <strong>{confirmDel.name}</strong> » sera <strong>définitivement supprimée</strong>.
              {confirmDel.status === 'sent'
                ? <> Tu perdras aussi son <strong>rapport</strong> (envois, taux d'ouverture, clics) — ces statistiques ne pourront pas être récupérées.</>
                : <> Ce brouillon et son contenu seront perdus.</>}
            </p>
            <div className="camp-del-actions">
              <button className="camp-del-cancel" onClick={() => setConfirmDel(null)} disabled={deletingCamp}>Annuler</button>
              <button className="camp-del-confirm" onClick={doDeleteCampaign} disabled={deletingCamp}>
                {deletingCamp ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Helper : détecte un numéro mobile suisse (076/077/078/079) ───────────────
function isSmartphone(phone) {
  // Mobile suisse (074–079) — brique partagée + testée (src/utils/dataInsights.js).
  return isSwissMobile(phone);
}

// ── Smart segment suggestions ────────────────────────────────────────────────
function buildSmartSuggestions(objective, contacts) {
  const now   = new Date();
  const now12 = new Date(); now12.setMonth(now12.getMonth() - 12);
  const curM  = now.getMonth() + 1;
  const nextM = curM === 12 ? 1 : curM + 1;

  // Helpers
  const active    = contacts.filter(c => c.status === 'active');
  const vip       = contacts.filter(isVip);
  const dormant   = contacts.filter(c => !c.last_interaction || new Date(c.last_interaction) < now12);
  const withBday  = contacts.filter(c => c.birth_date);
  const bdayMonth = withBday.filter(c => parseInt(c.birth_date.split('-')[1], 10) === curM);
  const bdayNext  = withBday.filter(c => parseInt(c.birth_date.split('-')[1], 10) === nextM);
  const roundAge  = withBday.filter(c => {
    const age = contactAge(c.birth_date);
    return age != null && ([30,40,50,60,70].includes(age) || [30,40,50,60,70].includes(age+1));
  });
  const hommes    = contacts.filter(c => c.gender === 'monsieur');
  const femmes    = contacts.filter(c => c.gender === 'madame');
  const withEmail = contacts.filter(c => c.email && c.gdpr_consent !== false);

  // Helper desc : affiche email + mobile SMS
  const segDesc = (list, prefix = '') => {
    const emails = list.filter(c => c.email).length;
    const mobiles = list.filter(c => isSmartphone(c.phone)).length;
    const parts = [];
    if (emails > 0) parts.push(`📧 ${emails}`);
    if (mobiles > 0) parts.push(`📱 ${mobiles} SMS`);
    return (prefix ? prefix + ' — ' : '') + (parts.join('  ·  ') || 'Aucun canal disponible');
  };

  // Build suggestions specific to objective
  const obj = (objective || '').toLowerCase();
  const sugg = [];

  // Anniversaires
  if (obj.includes('anniversaire') || obj.includes('birthday')) {
    if (bdayMonth.length > 0) sugg.push({
      id: 'bday_month', icon: '🎂', label: `Anniversaires de ${now.toLocaleDateString('fr-FR',{month:'long'})}`,
      desc: segDesc(bdayMonth),
      count: bdayMonth.length, filters: { type: 'birthday_month' }, highlight: true,
    });
    if (bdayNext.length > 0) sugg.push({
      id: 'bday_next', icon: '📅', label: `Anniversaires du mois prochain`,
      desc: segDesc(bdayNext, 'Anticipe dès maintenant'),
      count: bdayNext.length, filters: { type: '', category: null },
    });
    if (roundAge.length > 0) sugg.push({
      id: 'round', icon: '🎯', label: 'Âges ronds (30, 40, 50, 60…)',
      desc: segDesc(roundAge),
      count: roundAge.length, filters: { type: '' },
    });
  }

  // Fête des pères / homme
  if (obj.includes('père') || obj.includes('pere') || obj.includes('man')) {
    if (hommes.length > 0) sugg.push({
      id: 'hommes', icon: '👔', label: 'Tous tes clients Messieurs',
      desc: segDesc(hommes),
      count: hommes.length, filters: { type: 'active', gender: ['monsieur'] }, highlight: true,
    });
  }

  // Fête des mères / femme / Saint-Valentin
  if (obj.includes('mère') || obj.includes('mere') || obj.includes('valentin') || obj.includes('femme')) {
    if (femmes.length > 0) sugg.push({
      id: 'femmes', icon: '💐', label: 'Toutes tes clientes Mesdames',
      desc: segDesc(femmes),
      count: femmes.length, filters: { type: 'active', gender: ['madame'] }, highlight: true,
    });
    if (obj.includes('valentin') && hommes.length > 0) sugg.push({
      id: 'hommes_val', icon: '💝', label: 'Messieurs aussi — surprise pour elles',
      desc: segDesc(hommes),
      count: hommes.length, filters: { type: 'active', gender: ['monsieur'] },
    });
  }

  // VIP
  if (obj.includes('vip') || obj.includes('fidél') || obj.includes('meilleur')) {
    if (vip.length > 0) sugg.push({
      id: 'vip', icon: '⭐', label: 'Clients VIP',
      desc: segDesc(vip, 'Tes meilleurs'),
      count: vip.length, filters: { type: 'vip' }, highlight: true,
    });
    if (active.length > 0) sugg.push({
      id: 'active', icon: '✅', label: 'Tous les contacts actifs',
      desc: segDesc(active),
      count: active.length, filters: { type: 'active' },
    });
  }

  // Dormants / réactivation
  if (obj.includes('dormant') || obj.includes('réactiv') || obj.includes('reactiv') || obj.includes('manqu')) {
    if (dormant.length > 0) sugg.push({
      id: 'dormant', icon: '💤', label: 'Contacts silencieux depuis 12 mois+',
      desc: segDesc(dormant),
      count: dormant.length, filters: { type: 'dormant' }, highlight: true,
    });
  }

  // Noël / bonne année / saisonnière / promo / merci / événement / local → tous actifs par défaut
  if (sugg.length === 0) {
    if (active.length > 0) sugg.push({
      id: 'active', icon: '✅', label: 'Contacts actifs',
      desc: segDesc(active, 'Ta base engagée'),
      count: active.length, filters: { type: 'active' }, highlight: true,
    });
    sugg.push({
      id: 'all', icon: '👥', label: 'Toute la base',
      desc: segDesc(contacts),
      count: contacts.length, filters: { type: '' },
    });
  }

  // Toujours proposer "toute la base" si pas déjà là
  if (!sugg.find(s => s.id === 'all') && !obj.includes('dormant')) {
    sugg.push({
      id: 'all', icon: '👥', label: 'Toute la base',
      desc: `${withEmail.length} contacts avec e-mail`,
      count: contacts.length, filters: { type: '' },
    });
  }

  return sugg;
}

function SmartSegments({ objective, contacts, filters, setFilters }) {
  const suggestions = useMemo(
    () => buildSmartSuggestions(objective, contacts),
    [objective, contacts] // eslint-disable-line
  );

  if (!objective || suggestions.length === 0) return null;

  const isActive = (s) => {
    const f = s.filters;
    return (f.type ?? '') === (filters.type ?? '') &&
      JSON.stringify(f.gender ?? []) === JSON.stringify(filters.gender ?? []);
  };

  return (
    <div className="smart-seg-block">
      <div className="smart-seg-label">
        <span>🤖</span> Appee suggère — clique pour sélectionner la bonne cible
      </div>
      <div className="smart-seg-list">
        {suggestions.map(s => (
          <button
            key={s.id}
            className={`smart-seg-card ${isActive(s) ? 'active' : ''} ${s.highlight ? 'highlight' : ''}`}
            onClick={() => setFilters(f => ({
              ...f,
              type:   s.filters.type   ?? '',
              gender: s.filters.gender ?? [],
            }))}
          >
            <div className="smart-seg-icon">{s.icon}</div>
            <div className="smart-seg-body">
              <div className="smart-seg-name">{s.label}</div>
              <div className="smart-seg-desc">{s.desc}</div>
            </div>
            <div className="smart-seg-count">{s.count}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── CampaignBuilder (step 2) ────────────────────────────────────────────────
function CampaignBuilder({ contacts, filters, setFilters }) {
  const [preview, setPreview]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [advanced, setAdvanced]   = useState(false);
  const debounceRef               = useRef(null);

  // Unique cities, regions, cantons, categories from loaded contacts
  const cities      = useMemo(() => [...new Set(contacts.map(c => c.city).filter(Boolean))].sort(), [contacts]);
  const regions     = useMemo(() => [...new Set(contacts.map(c => c.region).filter(Boolean))].sort(), [contacts]);
  // Cantons SUISSES uniquement (codes officiels à 2 lettres) — on écarte les
  // départements français (Côte-d'Or, Doubs, Bas-Rhin…) qui traînent dans la donnée.
  const cantons     = useMemo(() => swissCantons(contacts), [contacts]);
  const categories  = useMemo(() => [...new Set(contacts.map(c => c.category).filter(Boolean))].sort(), [contacts]);

  const now12 = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth()-12); return d; }, []);
  const curMonth = new Date().getMonth() + 1;

  // Local fallback counts
  const localCount = useMemo(() => {
    let list = contacts;
    if (filters.type === 'dormant')        list = list.filter(c => !c.last_interaction || new Date(c.last_interaction) < now12);
    else if (filters.type === 'vip')       list = list.filter(c => (c.quality_score ?? 0) > 80);
    else if (filters.type === 'birthday_month') list = list.filter(c => c.birth_date && parseInt(c.birth_date.split('-')[1],10) === curMonth);
    if (filters.gender?.length)            list = list.filter(c => filters.gender.includes(c.gender || 'non_renseigne'));
    if (filters.age_min)                   list = list.filter(c => { if (!c.birth_date) return false; const age = Math.floor((Date.now() - new Date(c.birth_date)) / 31557600000); return age >= filters.age_min; });
    if (filters.age_max)                   list = list.filter(c => { if (!c.birth_date) return false; const age = Math.floor((Date.now() - new Date(c.birth_date)) / 31557600000); return age <= filters.age_max; });
    if (filters.city)                      list = list.filter(c => c.city === filters.city);
    if (filters.region)                    list = list.filter(c => c.region === filters.region);
    if (filters.canton)                    list = list.filter(c => c.canton === filters.canton);
    if (filters.category)                  list = list.filter(c => c.category === filters.category);
    const withPhone = list.filter(c => c.phone).length;
    const withSmart = list.filter(c => isSmartphone(c.phone)).length;
    return {
      total:           list.length,
      with_email:      list.filter(c => c.email).length,
      with_phone:      withPhone,
      with_smartphone: withSmart,
      non_smartphone:  withPhone - withSmart,
      rgpd_excluded:   list.filter(c => c.gdpr_consent === false).length,
    };
  }, [contacts, filters, now12, curMonth]);

  // Debounced API preview
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.previewSegment(filters);
        setPreview({ ...res, non_smartphone: (res.with_phone || 0) - (res.with_smartphone || 0) });
      } catch {
        setPreview(localCount);
      } finally { setLoading(false); }
    }, 600);
  }, [filters]); // eslint-disable-line

  const counts = preview || localCount;
  const eligible = (counts.total || 0) - (counts.rgpd_excluded || 0);

  const toggleGender = (g) => {
    const cur = filters.gender || [];
    setFilters(f => ({ ...f, gender: cur.includes(g) ? cur.filter(x => x !== g) : [...cur, g] }));
  };

  const GENDER_OPTS = [
    { value: 'monsieur',      label: 'Messieurs', color: '#2563EB', bg: '#EFF6FF' },
    { value: 'madame',        label: 'Mesdames',  color: '#DB2777', bg: '#FDF2F8' },
    { value: 'autre',         label: 'Autre',     color: '#7C3AED', bg: '#F5F3FF' },
    { value: 'non_renseigne', label: 'Non rens.', color: '#6B7280', bg: '#F9FAFB' },
  ];

  const PRESETS = [
    { value: '',              label: 'Tous les contacts',     icon: '👥' },
    { value: 'active',        label: 'Contacts actifs',       icon: '✅' },
    { value: 'dormant',       label: 'Clients dormants',      icon: '💤' },
    { value: 'vip',           label: 'Clients VIP',           icon: '⭐' },
    { value: 'birthday_month',label: 'Anniversaires du mois', icon: '🎂' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Live preview badge ── */}
      <div style={{ background: loading ? '#F9FAFB' : eligible > 0 ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${eligible > 0 ? '#BBF7D0' : '#FECACA'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 28 }}>{loading ? '⏳' : eligible > 0 ? '🎯' : '⚠️'}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: eligible > 0 ? '#166534' : '#991B1B' }}>
            {loading ? 'Calcul en cours…' : `${eligible} contact${eligible !== 1 ? 's' : ''} éligible${eligible !== 1 ? 's' : ''}`}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
            <span>📧 <b style={{color:'var(--ink)'}}>{counts.with_email || 0}</b> e-mail</span>
            <span>📱 <b style={{color:'var(--ink)'}}>{counts.with_smartphone || 0}</b> SMS mobile
              {counts.non_smartphone > 0 && <span style={{color:'#F59E0B', marginLeft:4}}>({counts.non_smartphone} fixe exclu)</span>}
            </span>
            {counts.rgpd_excluded > 0 && <span style={{ color: '#F59E0B' }}>⚠️ {counts.rgpd_excluded} exclu{counts.rgpd_excluded > 1 ? 's' : ''} RGPD</span>}
            {(() => {
              const mem = contacts.filter(c => {
                if (!c.relationship_memory) return false;
                if (filters.type === 'dormant') { const d = new Date(); d.setFullYear(d.getFullYear()-1); return !c.last_interaction || new Date(c.last_interaction) < d; }
                if (filters.type === 'vip') return (c.quality_score ?? 0) > 80;
                if (filters.type === 'active') return c.status === 'active';
                return true;
              });
              return mem.length > 0
                ? <span style={{ color: '#F59E0B', marginLeft: 8 }}>· 💡 {mem.length} mémoire{mem.length > 1 ? 's' : ''}</span>
                : null;
            })()}
          </div>
          {/* §23.5 — Appee advice based on segment size */}
          {!loading && (() => {
            if (eligible === 0) return (
              <div style={{ marginTop: 6, fontSize: 12, color: '#991B1B', fontStyle: 'italic' }}>
                💡 Appee : ta cible est vide — élargis les filtres pour trouver des contacts.
              </div>
            );
            if (eligible < 5) return (
              <div style={{ marginTop: 6, fontSize: 12, color: '#92400E', fontStyle: 'italic' }}>
                💡 Appee : seulement {eligible} contact{eligible > 1 ? 's' : ''} — élargis les critères pour plus d'impact.
              </div>
            );
            if (eligible > 500) return (
              <div style={{ marginTop: 6, fontSize: 12, color: '#4F46E5', fontStyle: 'italic' }}>
                💡 Appee : {eligible} contacts, c'est large ! Précise les filtres pour un message plus personnalisé.
              </div>
            );
            if (eligible >= 5 && eligible <= 50) return (
              <div style={{ marginTop: 6, fontSize: 12, color: '#166534', fontStyle: 'italic' }}>
                💡 Appee : cible précise et qualifiée — idéale pour un message très personnalisé.
              </div>
            );
            return (
              <div style={{ marginTop: 6, fontSize: 12, color: '#166534', fontStyle: 'italic' }}>
                💡 Appee : bonne taille de cible — le message atteindra suffisamment de contacts.
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Presets rapides ── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Segment prédéfini</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {PRESETS.map(p => (
            <button key={p.value}
              onClick={() => setFilters(f => ({ ...f, type: p.value }))}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, border: '1.5px solid', borderColor: (filters.type || '') === p.value ? '#6366F1' : '#E5E7EB', background: (filters.type || '') === p.value ? '#EEF2FF' : '#fff', color: (filters.type || '') === p.value ? '#4F46E5' : '#374151', fontSize: 13, fontWeight: (filters.type || '') === p.value ? 600 : 400, cursor: 'pointer', transition: 'all 0.15s' }}>
              {p.icon} {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Toggle avancé ── */}
      <button onClick={() => setAdvanced(a => !a)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6366F1', fontWeight: 600, fontSize: 13 }}>
        <span style={{ fontSize: 16 }}>{advanced ? '▲' : '▼'}</span>
        {advanced ? 'Masquer les filtres avancés' : '+ Filtres avancés (genre, âge, ville, canton…)'}
      </button>

      {advanced && (
        <div style={{ background: '#F9FAFB', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Genre */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Genre / Civilité</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {GENDER_OPTS.map(g => {
                const sel = (filters.gender || []).includes(g.value);
                return (
                  <button key={g.value} onClick={() => toggleGender(g.value)}
                    style={{ padding: '5px 14px', borderRadius: 20, border: '1.5px solid', borderColor: sel ? g.color : '#E5E7EB', background: sel ? g.bg : '#fff', color: sel ? g.color : '#374151', fontSize: 13, fontWeight: sel ? 600 : 400, cursor: 'pointer', transition: 'all 0.15s' }}>
                    {g.label}
                  </button>
                );
              })}
              {(filters.gender || []).length > 0 && (
                <button onClick={() => setFilters(f => ({ ...f, gender: [] }))} style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, cursor: 'pointer' }}>✕ Effacer</button>
              )}
            </div>
          </div>

          {/* Tranche d'âge */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Tranche d'âge</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="number" min={0} max={120} placeholder="Min" value={filters.age_min || ''}
                onChange={e => setFilters(f => ({ ...f, age_min: e.target.value ? parseInt(e.target.value) : null }))}
                style={{ width: 80, padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14 }} />
              <span style={{ color: 'var(--muted)' }}>à</span>
              <input type="number" min={0} max={120} placeholder="Max" value={filters.age_max || ''}
                onChange={e => setFilters(f => ({ ...f, age_max: e.target.value ? parseInt(e.target.value) : null }))}
                style={{ width: 80, padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14 }} />
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>ans</span>
              {(filters.age_min || filters.age_max) && (
                <button onClick={() => setFilters(f => ({ ...f, age_min: null, age_max: null }))} style={{ padding: '4px 10px', borderRadius: 20, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, cursor: 'pointer' }}>✕</button>
              )}
            </div>
          </div>

          {/* Ville + Région + Canton */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Ville</div>
              <select value={filters.city || ''} onChange={e => setFilters(f => ({ ...f, city: e.target.value || null }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, background: '#fff' }}>
                <option value="">Toutes les villes</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {regions.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Région</div>
                <select value={filters.region || ''} onChange={e => setFilters(f => ({ ...f, region: e.target.value || null }))}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, background: '#fff' }}>
                  <option value="">Toutes les régions</option>
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Canton</div>
              <select value={filters.canton || ''} onChange={e => setFilters(f => ({ ...f, canton: e.target.value || null }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, background: '#fff' }}>
                <option value="">Tous les cantons</option>
                {cantons.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Catégorie */}
          {categories.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Catégorie</div>
              <select value={filters.category || ''} onChange={e => setFilters(f => ({ ...f, category: e.target.value || null }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, background: '#fff' }}>
                <option value="">Toutes les catégories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {/* Résumé actif */}
          {(filters.gender?.length || filters.age_min || filters.age_max || filters.city || filters.region || filters.canton || filters.category) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 4 }}>
              {filters.gender?.map(g => <span key={g} style={{ padding: '3px 10px', borderRadius: 12, background: '#EEF2FF', color: '#4F46E5', fontSize: 12 }}>{GENDER_OPTS.find(o=>o.value===g)?.label}</span>)}
              {filters.age_min && <span style={{ padding: '3px 10px', borderRadius: 12, background: '#F0FDF4', color: '#166534', fontSize: 12 }}>≥ {filters.age_min} ans</span>}
              {filters.age_max && <span style={{ padding: '3px 10px', borderRadius: 12, background: '#F0FDF4', color: '#166534', fontSize: 12 }}>≤ {filters.age_max} ans</span>}
              {filters.city && <span style={{ padding: '3px 10px', borderRadius: 12, background: '#FFF7ED', color: '#92400E', fontSize: 12 }}>📍 {filters.city}</span>}
              {filters.region && <span style={{ padding: '3px 10px', borderRadius: 12, background: '#FFF7ED', color: '#92400E', fontSize: 12 }}>🗺 {filters.region}</span>}
              {filters.canton && <span style={{ padding: '3px 10px', borderRadius: 12, background: '#FFF7ED', color: '#92400E', fontSize: 12 }}>🏛 {filters.canton}</span>}
              {filters.category && <span style={{ padding: '3px 10px', borderRadius: 12, background: '#F5F3FF', color: '#7C3AED', fontSize: 12 }}>🏷 {filters.category}</span>}
              <button onClick={() => setFilters(f => ({ ...f, gender: [], age_min: null, age_max: null, city: null, region: null, canton: null, category: null }))}
                style={{ padding: '3px 10px', borderRadius: 12, background: '#FEF2F2', color: '#991B1B', fontSize: 12, border: 'none', cursor: 'pointer' }}>
                Tout effacer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function NewCampaign() {
  const { addNotification, setCampaigns, campaigns, contacts, tenant, showQuotaError, smtp } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const preset          = location.state?.preset          ?? null;
  const startStep       = location.state?.startStep       ?? 1;
  const strategyContext = location.state?.strategyContext  ?? null;
  const editId          = location.state?.editId          ?? null;

  // Campagne pré-remplie par beeappee (suggestion/marronnier) → on horodate le nom
  // « (JJ.MM.AAAA) » pour distinguer facilement les campagnes auto. Pas en édition
  // (le nom existe déjà) ni pour une campagne créée de zéro (pas de preset.name).
  const initialName = autoCampaignName(preset?.name, editId);

  const [step, setStep]           = useState(startStep);
  const [generating, setGenerating]     = useState(false);
  const [generatingSms, setGeneratingSms] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [sentCelebration, setSentCelebration] = useState(null); // {name, count, channel} après envoi
  const [memoryEnabled, setMemoryEnabled] = useState({});  // ✨ petit mot activé par contact (étape 4)
  const [memoryPreviewContact, setMemoryPreviewContact] = useState(null); // 👁 mail exact d'un contact à notes
  const [memoryLines, setMemoryLines] = useState({});   // ✨ clins d'œil générés/corrigés par contact
  const [weavingIds, setWeavingIds] = useState({});     // génération en cours par contact
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showWizardPreview, setShowWizardPreview] = useState(false);
  const [showImgPanel, setShowImgPanel] = useState(false);
  const [imgUrl, setImgUrl]             = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [form, setForm]       = useState({
    name:          initialName,
    objective:     preset?.objective ?? '',
    subject:       preset?.subject   ?? '',
    body:          preset?.body      ?? '',
    sms_body:      preset?.sms       ?? '',
    tone:          preset?.tone      ?? 'vouvoiement',
    channel:       preset?.channel   ?? 'email',
    schedule_type: 'now',
    scheduled_at:  '',
  });
  const [filters, setFilters] = useState(
    preset?.filters
      ? {
          type: preset.filters.type ?? '',
          gender: preset.filters.gender ?? [],
          age_min: preset.filters.age_min ?? null,
          age_max: preset.filters.age_max ?? null,
          city: preset.filters.city ?? null,
          region: preset.filters.region ?? null,
          canton: preset.filters.canton ?? null,
          category: preset.filters.category ?? null,
          // Bouclier anti-fatigue : exclut les contacts sollicités < 7 jours
          exclude_recent: preset.filters.exclude_recent ?? false,
          // Ciblage par liste d'e-mails (ex. participants d'un concours)
          emails: preset.filters.emails ?? null,
        }
      : { type: '', gender: [], age_min: null, age_max: null, city: null, region: null, canton: null, category: null, exclude_recent: false, emails: null }
  );

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // §15 — Contacts with relationship_memory matching current filters
  const now12m = useMemo(() => { const d = new Date(); d.setFullYear(d.getFullYear()-1); return d; }, []);
  const curMonth = new Date().getMonth() + 1;
  const filteredContacts = useMemo(() => {
    let list = contacts;
    if (filters.type === 'dormant')        list = list.filter(c => !c.last_interaction || new Date(c.last_interaction) < now12m);
    else if (filters.type === 'vip')       list = list.filter(c => (c.quality_score ?? 0) > 80);
    else if (filters.type === 'birthday_month') list = list.filter(c => c.birth_date && parseInt(c.birth_date.split('-')[1],10) === curMonth);
    else if (filters.type === 'active')    list = list.filter(c => c.status === 'active');
    // BI : réceptifs à convertir (réagissent mais pas encore attachés) & jamais contactés
    else if (filters.type === 'hot')       list = list.filter(c =>
      ((c.emails_clicked > 0) || (c.emails_opened > 0)) &&
      !(['vip', 'client vip', 'fidèle', 'fidele', 'partenaire'].includes((c.category || '').trim().toLowerCase()) || (c.feedback_count > 0)));
    else if (filters.type === 'never_contacted') list = list.filter(c => !(c.emails_sent > 0) && !c.last_email_sent);
    // Bouclier anti-fatigue : jamais 2 sollicitations en < 7 jours
    if (filters.exclude_recent)            list = list.filter(c => !c.last_email_sent || (Date.now() - new Date(c.last_email_sent).getTime()) > 7 * 86400000);
    if (filters.gender?.length)            list = list.filter(c => filters.gender.includes(c.gender || 'non_renseigne'));
    if (filters.city)                      list = list.filter(c => c.city === filters.city);
    if (filters.region)                    list = list.filter(c => c.region === filters.region);
    if (filters.canton)                    list = list.filter(c => c.canton === filters.canton);
    if (filters.category)                  list = list.filter(c => c.category === filters.category);
    if (filters.emails?.length) {
      const set = new Set(filters.emails.map(e => (e || '').toLowerCase()));
      list = list.filter(c => c.email && set.has(c.email.toLowerCase()));
    }
    return list;
  }, [contacts, filters, now12m, curMonth]);
  const memoryContacts = useMemo(() => filteredContacts.filter(c => c.relationship_memory), [filteredContacts]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { subject, body } = await api.generateMessage(form.objective, null, form.tone);
      set('subject', subject); set('body', body);
      addNotification('Message e-mail généré ✨');
    } catch (err) {
      showQuotaError(err);
      if (!err?.quotaExceeded) {
        set('subject', `Un message pour vous, {{prénom}}`);
        set('body', `Bonjour {{prénom}},\n\nNous espérons que vous allez bien.\n\n${form.objective ? `Dans le cadre de "${form.objective}", ` : ''}nous souhaitons vous partager un message.\n\nBien cordialement,\nL'équipe {{entreprise}}`);
        addNotification('Message e-mail généré ✨');
      }
    } finally { setGenerating(false); }
  };

  const handleGenerateSms = async () => {
    setGeneratingSms(true);
    try {
      // Passe le corps de l'e-mail si canal email+sms pour cohérence
      const emailBody = form.channel === 'email+sms' ? form.body : null;
      const res = await api.generateSmsMessage(form.objective, form.tone, emailBody);
      if (res.sms_body) set('sms_body', res.sms_body.slice(0, 320));
      addNotification('SMS généré ✨');
    } catch (err) {
      showQuotaError(err);
      if (!err?.quotaExceeded) {
        const fallback = `Bonjour {{prénom}}, ${form.objective ? form.objective + ' — ' : ''}nous avons pensé à vous ! — {{entreprise}}`.slice(0, 160);
        set('sms_body', fallback);
        addNotification('SMS généré ✨');
      }
    } finally { setGeneratingSms(false); }
  };

  // Dérive un SMS court depuis le corps e-mail (local, gratuit, sans IA).
  // Objectif : message NATUREL et court (1–2 phrases), pas l'e-mail tronqué en
  // plein milieu d'un mot, et SANS doubler le prénom.
  // smsFromEmailBody : brique partagée + testée (src/utils/sms.js).

  // Auto-remplit le SMS dès qu'on passe en e-mail+SMS avec un SMS encore vide :
  // l'app le fait, l'utilisateur ne clique pas. Le bouton « Générer depuis l'e-mail »
  // reste dispo pour une version IA affinée.
  const autoSmsRef = useRef(false);
  useEffect(() => {
    const wantsSms = form.channel === 'email+sms';
    if (!wantsSms) { autoSmsRef.current = false; return; }
    const emailReady = (form.body || '').trim().length > 0;
    const smsEmpty   = !(form.sms_body || '').trim();
    if (emailReady && smsEmpty && !autoSmsRef.current) {
      autoSmsRef.current = true;
      const derived = smsFromEmailBody(form.body);
      if (derived) set('sms_body', derived);
    }
  }, [form.channel, form.body, form.sms_body]); // eslint-disable-line react-hooks/exhaustive-deps

  const insertImgAtCursor = (url) => {
    const tag = `\n<img src="${url}" alt="" style="max-width:100%;height:auto;display:block;margin:12px auto;" />\n`;
    const ta = document.getElementById('email-body-ta');
    if (ta) {
      const start = ta.selectionStart ?? form.body.length;
      const end   = ta.selectionEnd   ?? form.body.length;
      const newVal = form.body.substring(0, start) + tag + form.body.substring(end);
      set('body', newVal);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + tag.length; ta.focus(); }, 0);
    } else {
      set('body', form.body + tag);
    }
    setImgUrl('');
    setShowImgPanel(false);
  };

  const handleImgFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.uploadCampaignImage(fd);
      insertImgAtCursor(res.url);
    } catch {
      addNotification('Erreur upload image', 'error');
    } finally {
      setUploadingImg(false);
      e.target.value = '';
    }
  };

  const bodyImages = useMemo(() => {
    const matches = [...(form.body || '').matchAll(/src="([^"]+)"/g)];
    return matches.map(m => m[1]).filter(u => u.startsWith('http'));
  }, [form.body]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const segment_filters = {
        type:     filters.type     || undefined,
        gender:   filters.gender?.length ? filters.gender : undefined,
        age_min:  filters.age_min  || undefined,
        age_max:  filters.age_max  || undefined,
        city:     filters.city     || undefined,
        region:   filters.region   || undefined,
        canton:   filters.canton   || undefined,
        category: filters.category || undefined,
        // ⚠️ Ces filtres étaient appliqués à l'AFFICHAGE mais perdus à l'ENVOI
        emails:   filters.emails?.length ? filters.emails : undefined,
        exclude_recent: filters.exclude_recent || undefined,
        // ✨ Petits mots activés par contact — ajoutés en P.S. à la fin de LEUR e-mail
        ...(Object.entries(memoryEnabled).some(([id, on]) => on && (memoryLines[id] || '').trim())
          ? { memory_lines: Object.fromEntries(Object.entries(memoryLines).filter(([id, v]) => memoryEnabled[id] && (v || '').trim())) }
          : {}),
      };

      const payload = {
        name:            form.name || 'Nouvelle campagne',
        objective:       form.objective,
        type:            'custom',
        channel:         form.channel || 'email',
        subject:         form.subject  || undefined,
        body:            form.body     || undefined,
        sms_body:        form.sms_body || undefined,
        tone:            form.tone,
        segment_filters,
        scheduled_at:    form.schedule_type === 'later' ? form.scheduled_at : undefined,
      };

      let campaign;
      if (editId) {
        campaign = await api.updateCampaign(editId, payload);
        setCampaigns(cs => cs.map(c => c.id === editId ? campaign : c));
      } else {
        campaign = await api.createCampaign(payload);
        setCampaigns(cs => [campaign, ...cs]);
      }

      let sendRes = null;
      if (form.schedule_type === 'now' && campaign?.id) {
        sendRes = await api.sendCampaign(campaign.id);
      }

      // Link campaign back to strategy (via API)
      if (strategyContext?.instanceId) {
        try {
          const instances = await api.getStrategyInstances();
          const strat = instances.find(s => s.instanceId === strategyContext.instanceId);
          if (strat) {
            const updatedStrat = {
              ...strat,
              linkedCampaigns: [
                ...(strat.linkedCampaigns || []),
                {
                  id: campaign.id,
                  name: campaign.name,
                  actionId: strategyContext.actionId,
                  actionTitle: strategyContext.actionTitle,
                  channel: strategyContext.channel,
                  createdAt: new Date().toISOString(),
                },
              ],
            };
            await api.updateStrategyInstance(strategyContext.instanceId, updatedStrat);
            // Sync cache local
            const updatedAll = instances.map(s => s.instanceId === strategyContext.instanceId ? updatedStrat : s);
            localStorage.setItem('appee_strategies_v2', JSON.stringify(updatedAll));
          }
        } catch(e) {}
      }

      if (strategyContext) {
        addNotification(`Campagne créée et liée à "${strategyContext.title}" ✅`);
      } else {
        addNotification(form.schedule_type === 'now' ? 'Campagne envoyée 🚀' : editId ? 'Campagne mise à jour ✅' : 'Campagne sauvegardée ✅');
      }

      // 🎉 Envoi immédiat = moment de fierté → écran de célébration (symétrique
      // de la fête de l'import) au lieu d'une redirection sèche vers la liste.
      if (form.schedule_type === 'now' && !strategyContext) {
        setSentCelebration({
          name: form.name || 'Ta campagne',
          count: filteredContacts.length,
          channel: form.channel,
          queued: !!sendRes?.queued,
        });
        setSaving(false);
        return;
      }

      navigate('/campaigns');
    } catch (e) {
      addNotification('Erreur : ' + (e.message || 'Impossible de créer la campagne'), 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="new-campaign-page">
      <Breadcrumb items={[
        { label: 'Campagnes', icon: '📣', onClick: () => navigate('/campaigns') },
        { label: editId ? 'Modifier la campagne' : 'Nouvelle campagne' },
      ]} />
      <div className="new-camp-hero">
        <div className="new-camp-hero-inner">
          <div className="new-camp-hero-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9l20-7z"/>
            </svg>
          </div>
          <div>
            <h1 className="new-camp-hero-title">{editId ? 'Modifier la campagne' : 'Nouvelle campagne'}</h1>
            <p className="new-camp-hero-sub">Étape {step} sur 4 — {['Objectif', 'Cible', 'Message', 'Envoi'][step - 1]}</p>
          </div>
        </div>
        <Link to="/campaigns"><button className="new-camp-hero-cancel">✕ Annuler</button></Link>
      </div>

      {/* Progress */}
      <div className="campaign-progress">
        {['Objectif', 'Cible', 'Message', 'Envoi'].map((s, i) => (
          <div key={i} className={`progress-step ${step > i + 1 ? 'done' : step === i + 1 ? 'active' : ''}`}>
            <div className="progress-dot">{step > i + 1 ? '✓' : i + 1}</div>
            <div className="progress-label">{s}</div>
          </div>
        ))}
      </div>

      <Card>
        {step === 1 && (
          <div className="campaign-step">
            {/* ── Sélecteur de canal ── */}
            <div className="step-header">
              <span className="step-header-num">1</span>
              <h3 className="step-title">Comment souhaitez-vous contacter vos clients ?</h3>
            </div>
            <div className="channel-picker">
              {[
                { id: 'email', icon: '📧', label: 'E-mail', sub: 'Newsletter, annonce, fidélisation', badge: null, color: '#5A35FF', bg: '#EEEAFF' },
                { id: 'sms',   icon: '📱', label: 'SMS',    sub: 'Message court, offre immédiate', badge: 'Crédits', color: '#7C3AED', bg: '#F5F3FF' },
                { id: 'email+sms', icon: '🚀', label: 'E-mail + SMS', sub: 'Double impact — mail riche + SMS rappel', badge: 'Recommandé', color: '#EC4899', bg: '#FDF2F8' },
              ].map(ch => (
                <button
                  key={ch.id}
                  className={`channel-card ${form.channel === ch.id ? 'selected' : ''}`}
                  style={{ '--ch-color': ch.color, '--ch-bg': ch.bg }}
                  onClick={() => set('channel', ch.id)}
                >
                  {ch.badge && <span className="channel-badge">{ch.badge}</span>}
                  <div className="channel-card-icon">{ch.icon}</div>
                  <div className="channel-card-label">{ch.label}</div>
                  <div className="channel-card-sub">{ch.sub}</div>
                  {form.channel === ch.id && <div className="channel-check">✓</div>}
                </button>
              ))}
            </div>

            <h3 className="step-title" style={{ marginTop: 24 }}>Quel est votre objectif ?</h3>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Campagnes métier</div>
            <div className="objectives-mini-grid">
              {objectives.filter(o => !o.seasonal).map(obj => (
                <button
                  key={obj.id}
                  className={`obj-mini-card ${form.objective === obj.title ? 'selected' : ''}`}
                  onClick={() => set('objective', obj.title)}
                >
                  <span>{obj.icon}</span>
                  <span>{obj.title}</span>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', margin: '16px 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🎄 Campagnes saisonnières</div>
            <div className="objectives-mini-grid">
              {objectives.filter(o => o.seasonal).map(obj => (
                <button
                  key={obj.id}
                  className={`obj-mini-card ${form.objective === obj.title ? 'selected' : ''}`}
                  onClick={() => set('objective', obj.title)}
                >
                  <span>{obj.icon}</span>
                  <span>{obj.title}</span>
                </button>
              ))}
            </div>
            <Input label="Nom de la campagne" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Anniversaires juin 2025" style={{ marginTop: 16 }} />
            <div style={{ marginTop: 20 }}>
              <Button disabled={!form.objective} onClick={() => setStep(2)}>Suivant →</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="campaign-step">
            <div className="step-header">
              <span className="step-header-num">2</span>
              <h3 className="step-title">Définissez votre cible</h3>
            </div>
            {preset && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#EDE9FF', border: '1px solid #A78BFA', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: '#5A35FF', marginBottom: 14 }}>
                {READY_TEMPLATES.find(t => t.objective === form.objective)?.icon ?? '🎯'} {form.name} — cible pré-configurée
              </div>
            )}
            {strategyContext && (
              <div className="strategy-campaign-banner">
                <span>🧠</span>
                <div>
                  <div className="scb-title">Campagne liée à la stratégie <strong>{strategyContext.title}</strong></div>
                  <div className="scb-sub">Action : {strategyContext.actionTitle} · Canal : {strategyContext.channel === 'email' ? '📧 Email' : strategyContext.channel === 'sms' ? '📱 SMS' : '🌐 Landing'}</div>
                </div>
              </div>
            )}
            <SmartSegments objective={form.objective} contacts={contacts} filters={filters} setFilters={setFilters} />
            <CampaignBuilder contacts={contacts} filters={filters} setFilters={setFilters} />

            {/* ── Audience count bar ── */}
            {(() => {
              const withEmail = filteredContacts.filter(c => c.email).length;
              const withPhone = filteredContacts.filter(c => c.phone).length;
              const reach = form.channel === 'sms' ? withPhone : form.channel === 'email+sms' ? Math.max(withEmail, withPhone) : withEmail;
              const reachPct = filteredContacts.length > 0 ? Math.round(reach / filteredContacts.length * 100) : 0;
              return (
                <div className="audience-count-bar">
                  <div className="acb-main">
                    <div className="acb-number">{filteredContacts.length}</div>
                    <div className="acb-label">contact{filteredContacts.length !== 1 ? 's' : ''} sélectionné{filteredContacts.length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="acb-reach-strip">
                    <div className="acb-reach-bar" style={{ '--r': `${reachPct}%` }} />
                  </div>
                  <div className="acb-chips">
                    {(form.channel === 'email' || form.channel === 'email+sms') && (
                      <span className="acb-chip acb-chip--email">📧 {withEmail} avec e-mail</span>
                    )}
                    {(form.channel === 'sms' || form.channel === 'email+sms') && (
                      <span className="acb-chip acb-chip--sms">📱 {withPhone} avec téléphone</span>
                    )}
                    <span className="acb-chip acb-chip--reach">🎯 {reachPct}% joignables</span>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {startStep === 1 && <Button variant="ghost" onClick={() => setStep(1)}>← Retour</Button>}
              <Button onClick={() => setStep(3)}>Suivant →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="campaign-step">
            <div className="step-header">
              <span className="step-header-num">3</span>
              <h3 className="step-title">Rédigez votre message</h3>
            </div>

            {/* Canal actif */}
            <div className="channel-recap-pill" style={{ '--ch-color': form.channel === 'email' ? '#5A35FF' : form.channel === 'sms' ? '#7C3AED' : '#EC4899' }}>
              <span>{form.channel === 'email' ? '📧 E-mail' : form.channel === 'sms' ? '📱 SMS' : '🚀 E-mail + SMS'}</span>
              <button onClick={() => setStep(1)}>Changer →</button>
            </div>

            {(form.channel === 'email' || form.channel === 'email+sms') && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: '#3730A3', margin: '0 0 12px', lineHeight: 1.5 }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>📤</span>
                <span>Ta campagne part via <strong>l'infrastructure d'envoi Appee</strong> (meilleure délivrabilité, et ça protège la réputation de ton adresse perso contre le volume), <strong>à ton nom</strong> — les réponses arrivent chez toi. Tes e-mails d'anniversaire, eux, partent de ta propre adresse.</span>
              </div>
            )}

            {preset?.body && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: '#065F46', margin: '8px 0 12px' }}>
                ✅ Message pré-rédigé chargé — personnalisez-le si besoin
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <Select label="Ton" value={form.tone} onChange={e => set('tone', e.target.value)}>
                <option value="vouvoiement">Vouvoiement</option>
                <option value="tutoiement">Tutoiement</option>
                <option value="auto">Automatique</option>
              </Select>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="secondary" icon="✨" loading={generating} onClick={handleGenerate} size="md">
                  Générer avec l'IA
                </Button>
              </div>
            </div>

            {/* Champs E-mail */}
            {(form.channel === 'email' || form.channel === 'email+sms') && (
              <div className="msg-channel-block">
                {form.channel === 'email+sms' && <div className="msg-channel-label">📧 Message e-mail</div>}
                <Input label="Objet de l'e-mail" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Ex: Joyeux anniversaire, {{prénom}} 🎂" />
                <div style={{ position: 'relative' }}>
                  <Textarea id="email-body-ta" label="Corps du message" value={form.body} onChange={e => set('body', e.target.value)} rows={8} placeholder="Votre message…" style={{ marginTop: 12 }} hint="Variables : {{prénom}}, {{nom}}, {{entreprise}}, {{ville}}, {{âge}}" />
                  {form.body && (
                    <div className="email-body-counter">
                      <span>{form.body.split(/\s+/).filter(Boolean).length} mots</span>
                      <span className="ebc-dot">·</span>
                      <span>{form.body.length} caract.</span>
                      {form.body.includes('{{prénom}}') && <span className="ebc-var-chip">{'✓ {{prénom}}'}</span>}
                    </div>
                  )}
                </div>

                {/* Barre image */}
                <div className="img-toolbar">
                  <button
                    type="button"
                    className={`img-toolbar-btn ${showImgPanel ? 'active' : ''}`}
                    onClick={() => setShowImgPanel(p => !p)}
                  >
                    📷 Insérer une image
                  </button>
                  {bodyImages.length > 0 && (
                    <div className="img-toolbar-thumbs">
                      {bodyImages.map((u, i) => (
                        <img key={i} src={u} alt="" className="img-toolbar-thumb" title={u} />
                      ))}
                    </div>
                  )}
                </div>

                {showImgPanel && (
                  <div className="img-panel">
                    <div className="img-panel-section">
                      <div className="img-panel-label">📁 Depuis votre appareil</div>
                      <label className={`img-upload-label ${uploadingImg ? 'loading' : ''}`}>
                        {uploadingImg ? '⏳ Upload en cours…' : '+ Choisir un fichier'}
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImgFileUpload} disabled={uploadingImg} />
                      </label>
                      <div className="img-panel-hint">JPG, PNG, GIF — max 5 Mo</div>
                    </div>
                    <div className="img-panel-divider">ou</div>
                    <div className="img-panel-section">
                      <div className="img-panel-label">🔗 Depuis une URL</div>
                      <div className="img-panel-url-row">
                        <input
                          className="img-panel-url-input"
                          type="url"
                          value={imgUrl}
                          onChange={e => setImgUrl(e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          onKeyDown={e => { if (e.key === 'Enter' && imgUrl.trim()) insertImgAtCursor(imgUrl.trim()); }}
                        />
                        <button
                          className="img-panel-url-btn"
                          disabled={!imgUrl.trim()}
                          onClick={() => insertImgAtCursor(imgUrl.trim())}
                        >
                          Insérer
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Champs SMS */}
            {(form.channel === 'sms' || form.channel === 'email+sms') && (
              <div className="msg-channel-block" style={{ marginTop: form.channel === 'email+sms' ? 20 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: form.channel === 'email+sms' ? 12 : 0 }}>
                  {form.channel === 'email+sms' && <div className="msg-channel-label" style={{ marginBottom: 0 }}>📱 Message SMS</div>}
                  {form.channel === 'sms' && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>Rédigez votre SMS</div>}
                  <Button variant="secondary" icon="✨" loading={generatingSms} onClick={handleGenerateSms} size="sm">
                    {form.channel === 'email+sms' && form.body ? 'Générer depuis l\'e-mail' : 'Générer avec l\'IA'}
                  </Button>
                </div>
                <div style={{ position: 'relative' }}>
                  <Textarea
                    label=""
                    value={form.sms_body}
                    onChange={e => set('sms_body', e.target.value.slice(0, 320))}
                    rows={4}
                    placeholder={`Ex: Bonjour {{prénom}}, ${form.objective || 'message court et percutant'}… — {{entreprise}}`}
                    hint="Variables : {{prénom}}, {{nom}}, {{entreprise}}"
                  />
                  <div style={{ position: 'absolute', bottom: 8, right: 12, fontSize: 11, fontWeight: 700, color: smsInfo(form.sms_body || '').segments > 1 ? '#DC2626' : '#9CA3AF' }}>
                    {smsInfo(form.sms_body || '').chars} car. · {smsInfo(form.sms_body || '').segments} SMS{smsInfo(form.sms_body || '').unicode ? ' · emoji ⚠️' : ''}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Button variant="ghost" onClick={() => setStep(2)}>← Retour</Button>
              <Button
                disabled={
                  (form.channel === 'email' && (!form.subject || !form.body)) ||
                  (form.channel === 'sms' && !form.sms_body) ||
                  (form.channel === 'email+sms' && (!form.subject || !form.body || !form.sms_body))
                }
                onClick={() => setStep(4)}
              >Suivant →</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="campaign-step">
            <div className="step-header">
              <span className="step-header-num">4</span>
              <h3 className="step-title">Planifier l'envoi</h3>
            </div>

            {/* 📮 Cohérence adresse d'envoi : jamais de blocage, juste la vérité
                sur l'expéditeur + le raccourci pour faire mieux */}
            {(form.channel === 'email' || form.channel === 'email+sms') && !smtp?.verified && (
              <div style={{ background: '#F5F3FF', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18 }}>📮</span>
                <span style={{ flex: 1, minWidth: 220 }}>
                  Ton e-mail partira via Appee (expéditeur <b>noreply@beeappee.com</b>) — ça fonctionne, mais tes clients ne verront pas ton adresse.
                </span>
                <button type="button" onClick={() => navigate('/settings?tab=smtp')}
                  style={{ border: '1.5px solid var(--line)', background: '#fff', color: 'var(--violet)', borderRadius: 999, padding: '7px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  Envoyer depuis MON adresse (2 min) →
                </button>
              </div>
            )}

            {/* ⚖️ Transparence consentement (nLPD/LCD) : qui part, qui est exclu,
                et le raccourci pour régulariser — wording prudent, jamais bloquant */}
            {(form.channel === 'email' || form.channel === 'email+sms') && (() => {
              const noConsent = filteredContacts.filter(c => c.gdpr_consent == null && !c.opted_out).length;
              const refused   = filteredContacts.filter(c => c.gdpr_consent === false || c.opted_out).length;
              if (!noConsent && !refused) return null;
              return (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '11px 16px', marginBottom: 16, fontSize: 12.5, color: '#78350F', lineHeight: 1.55 }}>
                  {refused > 0 && (
                    <div>🛡️ <b>{refused} contact{refused > 1 ? 's' : ''}</b> {refused > 1 ? 'ont refusé ou se sont désinscrits' : 'a refusé ou s\'est désinscrit'} — {refused > 1 ? 'ils sont' : 'il est'} <b>automatiquement exclu{refused > 1 ? 's' : ''}</b> de l'envoi.</div>
                  )}
                  {noConsent > 0 && (
                    <div style={{ marginTop: refused > 0 ? 4 : 0 }}>
                      ⚖️ <b>{noConsent} destinataire{noConsent > 1 ? 's' : ''} sans consentement enregistré</b> — l'envoi à tes clients existants est admis (lien de désinscription ajouté automatiquement), mais la loi suisse (nLPD) demande de pouvoir <b>prouver leur accord</b>.{' '}
                      <button type="button" onClick={() => window.open('/contacts', '_blank')}
                        style={{ border: 'none', background: 'none', color: 'var(--violet)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, padding: 0, textDecoration: 'underline' }}>
                        Régulariser en 1 clic →
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 💳 Analyse crédits SMS — ventes sans friction : si ça manque, pack en 1 clic */}
            {(form.channel === 'sms' || form.channel === 'email+sms') && (() => {
              const smsTargets = filteredContacts.filter(c => c.phone && !c.sms_opted_out).length;
              const credits = tenant?.sms_credits ?? 0;
              const missing = Math.max(0, smsTargets - credits);
              if (smsTargets === 0) return null;
              if (missing === 0) {
                return (
                  <div style={{ background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#065F46' }}>
                    ✅ <b>{smsTargets} SMS</b> à envoyer — il te reste <b>{credits}</b> crédits ({credits - smsTargets} après l'envoi).
                  </div>
                );
              }
              const recIdx = CAMPAIGN_SMS_PACKS.findIndex(p => p.credits >= missing);
              const idx = recIdx === -1 ? CAMPAIGN_SMS_PACKS.length - 1 : recIdx;
              const pack = CAMPAIGN_SMS_PACKS[idx];
              return (
                <div style={{ background: '#FFFBEB', border: '1.5px solid #FCD34D', borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
                    ⚠️ <b>Il te manque {missing} crédit{missing > 1 ? 's' : ''} SMS</b> — ta cible représente {smsTargets} SMS et il t'en reste {credits}.
                    Sans recharge, seuls les {credits} premiers partiront.
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.checkoutPack('sms', idx, '/campaigns/new');
                        if (res?.url) { window.location.href = res.url; return; }
                        addNotification('Paiement indisponible pour le moment.', 'error');
                      } catch (e) { addNotification(e.message || 'Paiement indisponible pour le moment.', 'error'); }
                    }}
                    style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800, fontSize: 13, color: '#fff', background: 'var(--violet, #5A35FF)', borderRadius: 999, padding: '11px 20px', whiteSpace: 'nowrap' }}
                  >
                    💳 Pack {pack.credits} SMS — {pack.price} CHF →
                  </button>
                </div>
              );
            })()}

            <div className="send-options-grid">
              {[
                { value: 'now',   icon: '🚀', label: 'Envoyer maintenant',      sub: 'Envoyé immédiatement à toute la cible',    color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7' },
                { value: 'later', icon: '📅', label: "Planifier l'envoi",        sub: 'Choisissez la date et l\'heure parfaites', color: '#3B82F6', bg: '#EFF6FF', border: '#93C5FD' },
                { value: 'draft', icon: '💾', label: 'Brouillon',                sub: 'Sauvegarder pour finaliser plus tard',     color: '#6B7280', bg: '#F9FAFB', border: '#D1D5DB' },
              ].map(t => (
                <button
                  key={t.value}
                  className={`send-option-card${form.schedule_type === t.value ? ' send-option-card--selected' : ''}`}
                  style={{ '--so-color': t.color, '--so-bg': t.bg, '--so-border': t.border }}
                  onClick={() => set('schedule_type', t.value)}
                >
                  <div className="send-option-icon">{t.icon}</div>
                  <div className="send-option-label">{t.label}</div>
                  <div className="send-option-sub">{t.sub}</div>
                  {form.schedule_type === t.value && <div className="send-option-check">✓</div>}
                </button>
              ))}
            </div>

            {/* Audience hero */}
            {(() => {
              const withEmail = filteredContacts.filter(c => c.email).length;
              const withPhone = filteredContacts.filter(c => c.phone).length;
              return (
                <div className="preview-audience-hero">
                  <div className="pah-left">
                    <div className="pah-number">{filteredContacts.length}</div>
                    <div className="pah-label">contacts ciblés</div>
                  </div>
                  <div className="pah-chips">
                    {(form.channel === 'email' || form.channel === 'email+sms') && (
                      <div className="pah-chip">📧 {withEmail} e-mails</div>
                    )}
                    {(form.channel === 'sms' || form.channel === 'email+sms') && (
                      <div className="pah-chip">📱 {withPhone} SMS</div>
                    )}
                    {form.objective && (
                      <div className="pah-chip pah-chip--obj">🎯 {form.objective}</div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Preview */}
            <div className="campaign-preview">
              <div className="preview-label">Récapitulatif</div>
              <div className="preview-row"><span>Objectif</span><span>{form.objective || '—'}</span></div>
              <div className="preview-row"><span>Nom</span><span>{form.name || '—'}</span></div>
              <div className="preview-row">
                <span>Cible</span>
                <span>
                  {filters.type || 'Tous'}
                  {filters.gender?.length ? ` · ${filters.gender.join(', ')}` : ''}
                  {filters.age_min || filters.age_max ? ` · ${filters.age_min||0}-${filters.age_max||'∞'} ans` : ''}
                  {filters.city ? ` · ${filters.city}` : ''}
                  {filters.region ? ` · ${filters.region}` : ''}
                  {filters.canton ? ` · ${filters.canton}` : ''}
                </span>
              </div>
              <div className="preview-row"><span>Canal</span><span>{form.channel === 'email' ? '📧 E-mail' : form.channel === 'sms' ? '📱 SMS' : '🚀 E-mail + SMS'}</span></div>
              {(form.channel === 'email' || form.channel === 'email+sms') && <div className="preview-row"><span>Objet</span><span>{form.subject || '—'}</span></div>}
              <div className="preview-row"><span>Ton</span><span>{form.tone}</span></div>
            </div>
            {/* §15 — Petits mots personnalisés : simple. Par défaut tout le monde
                reçoit LE MÊME message ; on liste ceux qui ont une note et on
                active (ou pas) un petit mot en plus, par contact. Zéro variable. */}
            {memoryContacts.length > 0 && (
              <div style={{ marginTop: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>💬</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E' }}>
                      {memoryContacts.length} contact{memoryContacts.length > 1 ? 's ont' : ' a'} une note personnelle
                    </div>
                    <div style={{ fontSize: 12, color: '#A16207', marginTop: 2 }}>
                      Par défaut, tout le monde reçoit le même message. Active « petit mot » pour ajouter une phrase personnelle à la fin de LEUR e-mail.
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                  {memoryContacts.map(c => {
                    const on = !!memoryEnabled[c.id];
                    return (
                      <div key={c.id} style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 160 }}>
                            <b style={{ color: '#78350F' }}>{c.first_name} {c.last_name}</b>
                            <span style={{ color: '#A16207', display: 'block' }}>💬 {c.relationship_memory}</span>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 700, color: on ? '#065F46' : '#92400E', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={on} style={{ accentColor: '#10B981' }}
                              onChange={async e => {
                                const checked = e.target.checked;
                                setMemoryEnabled(m => ({ ...m, [c.id]: checked }));
                                if (checked && !(memoryLines[c.id] || '').trim()) {
                                  setWeavingIds(w => ({ ...w, [c.id]: true }));
                                  try {
                                    const r = await api.weaveMemory(c.relationship_memory, form.subject || form.name || 'notre campagne');
                                    if (r?.line) setMemoryLines(m => ({ ...m, [c.id]: r.line }));
                                  } catch (err) { addNotification(err.message || 'Génération impossible — écris le mot toi-même.', 'error'); }
                                  finally { setWeavingIds(w => ({ ...w, [c.id]: false })); }
                                }
                              }} />
                            ✨ Petit mot
                          </label>
                          <button type="button" onClick={() => setMemoryPreviewContact(c)}
                            title={`Voir le mail exact que recevra ${c.first_name}`}
                            style={{ border: '1px solid #FDE68A', background: '#fff', color: '#92400E', borderRadius: 999, padding: '4px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                          >👁 Son mail</button>
                        </div>
                        {on && (
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                            <textarea
                              value={memoryLines[c.id] ?? ''}
                              onChange={e => setMemoryLines(m => ({ ...m, [c.id]: e.target.value }))}
                              placeholder={weavingIds[c.id] ? '✨ Je rédige le petit mot…' : 'Écris le petit mot (ou ↺ pour une proposition)'}
                              rows={2}
                              style={{ flex: 1, border: '1.5px solid #FDE68A', borderRadius: 8, padding: '6px 9px', fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }}
                            />
                            <button type="button" title="Proposer une autre formulation"
                              disabled={!!weavingIds[c.id]}
                              onClick={async () => {
                                setWeavingIds(w => ({ ...w, [c.id]: true }));
                                try {
                                  const r = await api.weaveMemory(c.relationship_memory, form.subject || form.name || 'notre campagne');
                                  if (r?.line) setMemoryLines(m => ({ ...m, [c.id]: r.line }));
                                } catch (err) { addNotification(err.message || 'Génération impossible.', 'error'); }
                                finally { setWeavingIds(w => ({ ...w, [c.id]: false })); }
                              }}
                              style={{ border: '1px solid #FDE68A', background: '#FFFBEB', color: '#92400E', borderRadius: 8, padding: '5px 9px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                            >{weavingIds[c.id] ? '⏳' : '↺'}</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {form.schedule_type === 'later' && (
              <div style={{ marginTop: 12 }}>
                <Input label="Date et heure d'envoi" type="datetime-local" value={form.scheduled_at} onChange={e => set('scheduled_at', e.target.value)} />
              </div>
            )}

            {/* Bouton aperçu */}
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#F8F7FF', border: '1px solid #E0D9FF', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22 }}>👁</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Vérifiez votre message avant d'envoyer</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Aperçu avec données exemple (Marie Dupont)</div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setShowWizardPreview(true)}>
                Prévisualiser
              </Button>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Button variant="ghost" onClick={() => setStep(3)}>← Retour</Button>
              <Button loading={saving} onClick={() => {
                // Planification : exiger une date valide dans le futur
                if (form.schedule_type === 'later') {
                  if (!form.scheduled_at || new Date(form.scheduled_at) <= new Date()) {
                    addNotification('Choisis une date et une heure d\'envoi dans le futur 📅', 'error');
                    return;
                  }
                }
                // Envoi immédiat : on confirme d'abord (action définitive)
                if (form.schedule_type === 'now') {
                  setShowSendConfirm(true);
                  return;
                }
                handleSave();
              }}>
                {form.schedule_type === 'now' ? '🚀 Envoyer la campagne' : form.schedule_type === 'draft' ? '💾 Sauvegarder' : '📅 Planifier'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* 👁 Aperçu du mail EXACT d'un contact à note relationnelle */}
      {memoryPreviewContact && (
        <CampaignPreviewModal
          campaign={{
            name:     form.name || 'Aperçu campagne',
            channel:  'email',
            subject:  form.subject,
            body:     form.body,
            sms_body: form.sms_body,
          }}
          companyName={tenant?.name}
          contact={memoryPreviewContact}
          memoryLine={(memoryLines[memoryPreviewContact.id] || '').trim() || null}
          onClose={() => setMemoryPreviewContact(null)}
        />
      )}

      {/* Preview modal pour le wizard */}
      {showWizardPreview && (
        <CampaignPreviewModal
          campaign={{
            name:     form.name || 'Aperçu campagne',
            channel:  form.channel,
            subject:  form.subject,
            body:     form.body,
            sms_body: form.sms_body,
          }}
          companyName={tenant?.name}
          onClose={() => setShowWizardPreview(false)}
        />
      )}

      {/* Confirmation avant envoi immédiat — action définitive */}
      <ConfirmModal
        open={showSendConfirm}
        title="Prêt à envoyer ? 🚀"
        message={(() => {
          const nEmail = filteredContacts.filter(c => c.email).length;
          const nPhone = filteredContacts.filter(c => c.phone).length;
          const reach = form.channel === 'sms' ? nPhone
                      : form.channel === 'email+sms' ? Math.max(nEmail, nPhone)
                      : nEmail;
          const moyen = form.channel === 'sms' ? 'SMS'
                      : form.channel === 'email+sms' ? 'e-mail + SMS' : 'e-mail';
          return `Ton message part par ${moyen} à ${reach} personne${reach > 1 ? 's' : ''}. Une fois parti, on ne peut plus le rappeler — mais promis, il est prêt et joli !`;
        })()}
        confirmLabel="Oui, on envoie !"
        cancelLabel="Pas encore"
        onConfirm={() => { setShowSendConfirm(false); handleSave(); }}
        onCancel={() => setShowSendConfirm(false)}
      />

      {/* 🎉 Célébration post-envoi — portail (règle : jamais de modale clippée) */}
      {sentCelebration && createPortal(
        <div className="camp-sent-backdrop" onClick={() => { setSentCelebration(null); navigate('/campaigns'); }}>
          <div className="camp-sent-card" onClick={e => e.stopPropagation()}>
            <div className="camp-sent-confetti" aria-hidden="true">🎉</div>
            <h2 className="camp-sent-title">C'est parti !</h2>
            <p className="camp-sent-sub">
              « {sentCelebration.name} » est en route vers{' '}
              <b>{sentCelebration.count} client{sentCelebration.count > 1 ? 's' : ''}</b>
              {' '}par {sentCelebration.channel === 'sms' ? 'SMS' : sentCelebration.channel === 'email+sms' ? 'e-mail + SMS' : 'e-mail'}.
            </p>
            <div className="camp-sent-next">
              <span className="camp-sent-next-lbl">Et maintenant ?</span>
              {sentCelebration.queued
                ? <>Tes e-mails partent <b>par vagues</b> dans les prochaines minutes — c'est ce qui protège ta réputation d'expéditeur. Je mets les statistiques à jour au fur et à mesure, tu n'as plus rien à faire 🧞</>
                : <>Les premières ouvertures arrivent en général dans l'heure — je te préparerai le rapport avec qui a lu et qui a cliqué. Tu n'as plus rien à faire 🧞</>}
            </div>
            <div className="camp-sent-actions">
              <button className="camp-sent-primary" onClick={() => { setSentCelebration(null); navigate('/campaigns'); }}>
                Voir mes campagnes →
              </button>
              <button className="camp-sent-ghost" onClick={() => { setSentCelebration(null); navigate('/dashboard'); }}>
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
